import rawSurfaces from '../../content/tuning/surfaces.json';
import rawTuning from '../../content/tuning/movement.json';
// Type-only: erased — the tuning pane MODULE is reachable only via the DEV_TUNING dynamic import.
import type { DebugOpts } from '../debug/panel';
// Type-only: erased — the badge module is reachable only via the same DEV fold.
import type { EditorBadgeHandle } from '../debug/editorBadge';
// Type-only: erased. The perf-bar MODULE is reachable only via the DEV dynamic import below.
import type { PerfBarHandle } from '../debug/perfBar';
// Type-only: erased — Tweakpane rides the lazy tuning pane and the editor chunk, never the entry.
import type { Pane } from 'tweakpane';
// Type-only: erased at compile time — the editor MODULE is reachable only via dynamic import.
import type { Editor } from '../editor/editor';
// Value import of the GATE only (a pure two-line predicate) — never of the editor module itself.
import { editorEnabled } from '../editor/gate';
import { createStaticBatcher, resolveCellM, resolveMinCullTris } from '../render/batching/staticBatcher';
import { EntityHost } from '../entities/host';
// P1.5·C: the ONE place `src/assets` and `src/entities` meet (ADR-0001's boundary runs between
// them, so neither imports the other and mount wires two one-way callbacks).
import { createLibraryEntities, type LibraryEntities } from './librarySpawn';
import { createSummonPlaceholder } from './summonPlaceholder';
// Type-only: erased — the exploration vertical is reachable only via loader.ts's dynamic import.
import type { ExplorationHandle } from '../exploration/loader';
// Type-only: erased — the LOADING SURFACE (L4) is reachable only via the dynamic import at the top
// of mount(). A value import would drag every src/ui token, string and layer into the eager entry
// chunk, which `scripts/checkBundle.mjs` fails the build over.
import type { LoadingNote, LoadingScreen } from '../ui/loading/loading';
// Value import of the GATE only (a pure predicate over the sub-table keys) — mirrors editorEnabled.
import { explorationRequired } from '../exploration/gate';
// Type-only: erased — the MAIN MENU vertical (plan §4) is reachable only via its loader's dynamic
// import. A value import would drag the UI foundation into the eager entry chunk.
import type { MenuActionId, MenuHandle } from '../ui/menu/loader';
// Value import of the MENU GATE only. It is the one file under `src/ui/**` eager code may import:
// a pure predicate over the menu sub-table's keys, importing nothing from the UI foundation.
import { menuRequired } from '../ui/menu/gate';
// Type-only: erased — the Phase-0 companion spike is reachable only via its dev-gated dynamic import.
import type { AxonSpikeHandle } from '../debug/axonSpike';
import { entityTypeNames } from '../entities/registry';
import type { EntityInstance, PlayerSnapshot } from '../entities/types';
import { GamepadSource } from '../input/gamepad';
import { KeyboardSource } from '../input/keyboard';
import { mergeTickInputs } from '../input/merge';
// The input-ownership seam (eager-safe door; src/ui/layers.ts is the owner that publishes into
// it). EVERY global key below asks this before acting AND before preventDefault().
import { uiHasFocus } from '../input/ownership';
import { MouseLookSource } from '../input/mouse';
// EAGER-SAFE by the same test as `net/axon/gate`: a dependency-free string parser over a
// boot-time URL. It pulls no transport, no token provider and no queue into the main chunk,
// and the launch code has to be read and SCRUBBED before the exploration chunk exists.
import { hashWithoutLaunch, parseLaunchFragment } from '../net/axon/launchFragment';
import { RapierCollisionService } from '../physics/rapierService';
import { applyShadowFlags } from '../render/lights/shadowFlags';
import { createScene } from '../presentation/scene';
import { Sfx } from '../presentation/sfx';
import { predictJumpArc } from '../sim/arc';
import { deriveFeedback, type FeedbackEvent } from '../sim/feedback';
import {
  FIXED_DT,
  MAX_TICKS_PER_FRAME,
  createMotionState,
  speedXZ,
  stepMotion,
} from '../sim/motion';
import {
  MovementTuningSchema,
  derive,
  getPreset,
  loadSurfaces,
  loadTuningFile,
  type MovementTuning,
} from '../sim/tuning';
import { clamp, v3 } from '../sim/vec';
import { collisionSidecarUrl, meshAssetUrl, resolveAssetUrl as resolveWorldAssetUrl } from '../world/assets';
// M3: the world material catalog + the texture resolver it was written to receive (G1/G2).
import { materialFor } from '../render/materials/catalog';
import { makeTextureResolver } from '../render/materials/worldTextures';
/*
 * ── ⭐ P1 · THE ASSET LIBRARY (ADR-0003) — EAGER, AND CONSTRUCTED EXACTLY ONCE ──────────────────
 *
 * The third distribution track's runtime half. Eager for the reason plan §4.1 gives: the character
 * is a library asset now and it is acquired at boot, so a lazy service would be one round trip
 * behind the thing it exists to load. `src/assets/core/**` is pure arithmetic (`PURE_DIRS`) and
 * `service.ts` is the ONE stateful object; every non-deterministic input it has — fetch, clock,
 * VRAM, the registry's class names, the decoder formats — is injected from HERE, which is what
 * makes every lifetime claim in `tests/assets/**` a Node golden instead of a claim about a browser.
 */
import {
  createAssetService,
  resolveLibraryOrigin,
  vramAccountantAdapter,
  type AssetService,
  type LibraryStats,
} from '../assets';
import { VramAccountant } from '../render/assets/vramAccountant';
// L3 · the load-phase ladder (PURE). Eager on purpose: `mount.ts` is eager, and the first rung is
// stamped on the first line of `mount()` — a lazily imported model would miss the window it exists
// to measure. A few hundred bytes of pure logic; `MAIN_BUDGET_KB` does not move.
import { LoadTracker, determinate, type LoadPhase } from '../world/loadPhase';
import {
  describeLibraryResidency,
  describeResidency,
  libraryResidency,
  requiredResidents,
  residency,
  worldPlayable,
} from '../world/readiness';
// The shared yield + budget. `entities/staticMesh.ts` uses the same pair for the per-ASSET walk;
// this file uses it for the per-PLACEMENT bind. One definition — see that module's header.
import { LOADING_BUDGET_MS, YIELD_BUDGET_MS, yieldTask } from '../world/yieldFrame';
import { placementPose } from '../world/collision';
import { checkRequires, mergeSurfaces, parseWorld } from '../world/schema';
import { resolveSpawn } from '../world/spawnPoint';
import { createPresetSwap, PRESET_SWAP_LABEL } from './presetSwap';

/**
 * Composition root. Exposed as mount(el, config) from day one so the Fronterra web app can host
 * the game later without any restructure (integration is a mount, not a merge — architecture D4.2).
 */
export interface MountConfig {
  /** Reserved for the host: auth token, world id, quality override. Unused in the sandbox. */
  [key: string]: unknown;
}

export interface MountedGame {
  unmount(): void;
}

/**
 * The ONLY build-env values this module reads, named ONE BY ONE on purpose.
 *
 * SECURITY: never hand `import.meta.env` to anything wholesale. Vite statically replaces each
 * `import.meta.env.X` it can see, but a wholesale reference forces it to materialize the WHOLE
 * env object into the bundle — every `VITE_*` var with it. That is not hypothetical: it inlined
 * this repo's local dev credentials into `dist/` on 2026-08-27 (local only, never committed).
 * Credentials are now non-`VITE_` (server-side only) AND `scripts/checkBundle.mjs` fails the
 * build if any `.env.local` value reaches the output — but this narrow read is the first fence.
 */
const BUILD_ENV = {
  DEV: import.meta.env.DEV,
  VITE_ENABLE_EDITOR: import.meta.env.VITE_ENABLE_EDITOR,
} as const;

/**
 * DEV-ONLY diagnostics seam (`window.__fwg`, src/debug/devHook.ts) — the Playwright harness's one
 * window into the running game (tests/e2e/).
 *
 * Deliberately NOT a function-call gate like `editorEnabled(BUILD_ENV)`: that call is opaque to
 * Rollup, which is why the editor chunk is still EMITTED in a prod build (unreachable-not-secret,
 * ADR-0002). Test scaffolding does not get that bar — it must be ABSENT. Both operands here are
 * compile-time literals (`import.meta.env.DEV` is replaced by Vite, `__FWG_DEV_HOOK__` by the
 * `define` in vite.config.ts), so this folds to `false` and the whole branch below — the dynamic
 * import, the chunk, the `__fwg` marker — disappears. scripts/checkBundle.mjs fails the build if
 * any of it survives.
 */
const DEV_HOOK = import.meta.env.DEV || __FWG_DEV_HOOK__;

/**
 * DEV-ONLY backend controls (X2-S5.5, src/debug/devBackend.ts): the MOCK/LIVE badge, the
 * backend/fixture pickers in the tuning pane, and the human-clicked backend debug buttons.
 *
 * A single compile-time literal, for the devHook reason: it must FOLD, not merely be unreachable.
 * Everything behind it either names a dev-server endpoint that does not exist in production
 * (`/api/__dev-auth/debug`) or names fixture sets that must not ship. Unlike DEV_HOOK there is no
 * `__FWG_DEV_HOOK__` escape: the dev-server middleware these buttons call only exists under
 * `vite dev`, so enabling them in a built artifact could not work anyway.
 */
const DEV_BACKEND = import.meta.env.DEV;

/**
 * DEV-ONLY GAMEPLAY TUNING PANE (EDITOR v2 · S4, `src/debug/panel.ts`) and the editor's corner
 * badge (`src/debug/editorBadge.ts`).
 *
 * The pane WAS EAGER — a static import, so all of Tweakpane and every tuning string shipped in the
 * main chunk, and it docked top-right over whatever the game drew there. It is a gameplay-DEV
 * surface: hidden unless `?tuning=1` or F3 (`onTuning`, gated on ownership like every global key),
 * hidden while the editor is open (the editor's right dock is where it docks), and its
 * `Open editor` button is gone — the editor has F2, `__fwg.openEditor()` and the badge.
 *
 * A single compile-time literal, for the DEV_HOOK reason: it must FOLD, not merely be unreachable.
 * `scripts/checkBundle.mjs` (c9) fails the build if the pane, the badge, their markers or Tweakpane
 * reach the entry chunk — and the entry ceiling was LOWERED by the measured delta in the same commit.
 */
const DEV_TUNING = import.meta.env.DEV;

/**
 * ⚠️ THROWAWAY — the PHASE-0 AXON COMPANION SPIKE (`?axon-spike=1`), deleted with the gate.
 *
 * A single compile-time literal, for the DEV_HOOK reason: it must FOLD, not merely be
 * unreachable. Everything behind it is a go/no-go prototype — a primitive hull on the follow
 * springs, the analytic world query, the screen-space yield rule — and none of it may reach a
 * player or grow the main chunk by a byte. `scripts/checkBundle.mjs` fails the build if the
 * chunk or its marker survives, exactly as it does for the dev hook.
 */
const AXON_SPIKE = import.meta.env.DEV;

/**
 * DEV-ONLY LIGHTING-STATE BADGE (owner direction #4, `src/debug/giBadge.ts`): is this world's
 * baked lighting current, missing, stale, or did the bake fail?
 *
 * A single compile-time literal, for the DEV_HOOK reason: it must FOLD, not merely be unreachable.
 * It is authoring scaffolding for the person editing a level — a player has no bake to be stale —
 * and `scripts/checkBundle.mjs` fails the build if the chunk or its marker survives.
 */
const GI_BADGE = import.meta.env.DEV;

const MOUSE_SENS = 0.0025;

/**
 * TEMPORARY skin recolor, `?skin=<name>` — see the rationale at its one consumer,
 * characterView.ts's material assignment. This is NOT the real cosmetics system: the server
 * already defines one (`CosmeticReward` in net/axon/model.ts, surfaced as a completion-screen
 * tease), and that system will want its own module, identity-scoped persistence and real garment
 * assets — none of which exist yet. This is a flat recolor of the one untextured mesh the
 * character actually is today, gated behind a URL flag so it stays trivially removable and is
 * never confused for the shipped thing. Unknown/absent name -> undefined -> the shipped color
 * (hard rule 6: degrade, never throw).
 */
const SKIN_PALETTE: Record<string, number> = {
  crimson: 0xd0342c,
  gold: 0xd4af37,
  violet: 0x8e44ad,
  emerald: 0x1abc9c,
  obsidian: 0x2c2c34,
  ice: 0xaee6ff,
};

function skinFromSearch(search: string): number | undefined {
  const name = new URLSearchParams(search).get('skin');
  return name ? SKIN_PALETTE[name] : undefined;
}

function tuningFromHash(): Partial<MovementTuning> | null {
  const h = location.hash;
  if (!h.startsWith('#p=')) return null;
  try {
    const parsed = MovementTuningSchema.safeParse(JSON.parse(atob(h.slice(3))));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export async function mount(el: HTMLElement, _config: MountConfig = {}): Promise<MountedGame> {
  /*
   * ── ⭐ L3 · THE LOAD PHASE STARTS ON THE FIRST LINE OF mount(), AND NOWHERE ELSE ───────────────
   *
   * `src/world/loadPhase.ts` carries the argument. The short version: UE is `LoadMap` → loading
   * screen → `BeginPlay`, and until this line the boot had exactly ONE observable boundary in it
   * (`worldResident`, the physics gate) — the LAST of six. Everything before it was a straight
   * line of awaits with no name, no stamp and no way for a test to say where a boot actually was.
   *
   * `performance.now()` is ms since NAVIGATION (the document's time origin), so every `atMs` in
   * `history` is directly comparable with a Playwright `page.goto` and with each other. The
   * tracker owns no clock precisely so this stays the one place a clock is read.
   */
  const nowMs = (): number => performance.now();
  const load = new LoadTracker(nowMs());
  /**
   * One console line per rung — the channel this repo already trusts for boot facts (the gate's
   * own `[world] playable — …` prints the same way). It is also the REACHABILITY tell: if a boot
   * prints no `[load]` lines, the model exists and nothing calls it, which is this repo's named
   * eighteen-times failure and exactly what this stage is fenced against.
   */
  const announce = (before: LoadPhase, after: LoadPhase): LoadPhase => {
    if (after !== before) console.info(`[load] ${after} at ${Math.round(nowMs())} ms`);
    return after;
  };
  /*
   * ── ⭐ L4 · THE PLATE GOES UP ON THE SAME TURN THE WORLD FETCH STARTS ──────────────────────────
   *
   * `src/ui/**` must stay unreachable from eager code (checkBundle asserts the token hex, the
   * layers marker and the strings catalog are all absent from the entry chunk), so mount reaches
   * the surface the way it reaches the exploration vertical: ONE dynamic import.
   *
   * ⚠️ AND IT IS DELIBERATELY NOT AWAITED AHEAD OF THE FETCH. Both start on this turn and neither
   * waits for the other — adding a round trip to every boot in order to show a loading screen
   * would be the joke version of this feature. The chunk is a few KB from the same origin and
   * lands well before `world.json` on any realistic connection; if it does not, the plate simply
   * appears a few ms later and the boot is unaffected.
   *
   * A FAILED import is swallowed to null: rule 6, and the fallback is exactly what shipped before
   * this existed — the boot carries on with no plate rather than dying inside its own loading
   * screen.
   */
  let screen: LoadingScreen | null = null;
  /** L4 · has the plate been handed its exit? Lowered again by a RESTART — see `acquireScreen`. */
  let screenLeaving = false;
  /*
   * ⭐ THE BOOT WATCHDOG, AND IT IS ARMED HERE BECAUSE THAT IS WHAT "NEVER A DEAD END" MEANS.
   *
   * `loadingScreen.ts`'s header promises the plate is unmounted by `playing` — "which mount()'s
   * 20 s deadline guarantees". It does not: that deadline is created inside `spawnEntities()`,
   * which is three awaits downstream (the world fetch, `initPhysics`, the renderer's `init()`).
   * A stalled CDN, a wedged `requestAdapter` or an offline tab therefore leaves an opaque
   * full-viewport plate up FOREVER, holding the keyboard, with no Try again and no sentence —
   * which is strictly worse than the dark rectangle it replaced, and is exactly the dead end the
   * surface was built to be incapable of.
   *
   * So the window is covered from the first turn. This timer bounds the run-up; `spawnEntities()`
   * CLEARS it as its first act and arms the residency deadline in its place, so there is never
   * more than one live and the two cannot both fire. Its arm is `block()`, not `openGateAnyway` —
   * a hang here means there is no world to open a gate onto.
   */
  const BOOT_DEADLINE_MS = 30_000;
  let bootTimer: ReturnType<typeof setTimeout> | null = setTimeout(() => {
    bootTimer = null;
    console.error(
      `[load] the world did not finish loading within ${BOOT_DEADLINE_MS / 1000}s — ` +
        'nothing was spawned, so there is no level to start with a hole in it. Showing the ' +
        'blocked plate instead of holding an opaque screen forever.',
    );
    screen?.block();
  }, BOOT_DEADLINE_MS);
  /**
   * The one-off sentence that outranks the rung line (`LoadingNote`). `fellBack` is NEWS and is
   * cleared by the next rung; `partial` and `slow` are the gate's own verdict and stay up for the
   * fade, because they describe the level the player is about to be standing in.
   */
  let loadNote: LoadingNote = '';
  /**
   * The `streaming` counters, read through a hole because they do not EXIST yet: `readResidency`
   * needs `world` and `physics`, which are two and three awaits away, and the first two rungs are
   * stamped before either. The stub answers honestly (nothing required, nothing bound, opaque) and
   * is replaced with the real fold the moment there is one.
   */
  let loadCounts = (): { determinate: boolean; bound: number; required: number } => ({
    determinate: false,
    bound: 0,
    required: 0,
  });
  const pushScreen = (): void => {
    if (!screen) return;
    const c = loadCounts();
    screen.update({
      phase: load.phase,
      determinate: c.determinate,
      bound: c.bound,
      required: c.required,
      note: loadNote,
    });
    // `fellBack` is a one-off: shown at the moment of the substitution, and the next rung takes
    // the line back. A permanent "we could not open that level" over a level that opened fine is
    // the kind of copy a child reads as "this is broken".
    if (loadNote === 'fellBack') loadNote = '';
  };
  /*
   * ⭐ ONE ACQUISITION PATH, AND IT IS RE-ENTRANT BECAUSE THE LADDER IS.
   *
   * `spawnEntities()` is the ONE re-entry into the load phase (L3), and after F2 -> Play it holds
   * the sim frozen for exactly as long as the boot did — with `screenLeaving` latched, so the
   * surface whose entire job is to cover that window stayed down and the creator watched a dead
   * world instead. A restart re-shows it. The old handle is DISPOSED rather than left to finish
   * its own fade: `release()` unmounts at the end of 620 ms, and a Play pressed inside that window
   * would otherwise leave two plates on one layer.
   */
  const acquireScreen = async (): Promise<void> => {
    const m = await import('../ui/loading/loading');
    if (screen && screenLeaving) screen.dispose();
    screenLeaving = false;
    screen = m.showLoadingScreen();
    // The chunk may land after a rung or two has already been stamped; paint what is true NOW
    // rather than the boot rung it would otherwise open on.
    pushScreen();
  };
  /*
   * ⚠️ DELIBERATELY NOT AWAITED (see above) and swallowed to null on failure: rule 6, and the
   * fallback is exactly what shipped before the plate existed.
   *
   * ⚠️ AND IT IS IDEMPOTENT, WHICH THE BOOT PATH REQUIRES RATHER THAN MERELY LIKING: the import is
   * in flight for the first few ms, so `screen` is still null when `spawnEntities()` runs its own
   * re-show check — and an unguarded second call would mount a SECOND plate on the one layer, on
   * every fast boot. `screenPending` is the in-flight half; `screen && !screenLeaving` is the
   * already-up half.
   */
  let screenPending = false;
  const showScreen = (): void => {
    if (screenPending || (screen !== null && !screenLeaving)) return;
    screenPending = true;
    void acquireScreen()
      .catch((err: unknown) => {
        console.warn('[load] loading screen unavailable — booting without it:', err);
      })
      .finally(() => {
        screenPending = false;
      });
  };
  showScreen();
  const toPhase = (next: LoadPhase): void => {
    const before = load.phase;
    announce(before, load.to(next, nowMs()));
    pushScreen();
  };
  // E0.5 (ADR-0001): the world arrives as DATA over the network — the pilot's distribution seam.
  // Nothing may import world.json statically; it is public/ static data, fetched at boot.
  // A3: `?world=<id>` selects the world; any failure falls back to sandbox-01 LOUDLY, not fatally.
  const fetchWorld = async (id: string) => {
    const res = await fetch(`/worlds/${id}/world.json`);
    if (!res.ok) throw new Error(`world.json fetch failed: ${res.status} ${res.statusText}`);
    const w = parseWorld(await res.json());
    // Capability gate: refuse loudly — an old runtime must never half-load a newer world.
    const caps = checkRequires(w, entityTypeNames());
    if (!caps.ok) {
      throw new Error(
        `world '${w.worldId}' requires entity classes this runtime does not have: ` +
          `[${caps.missing.join(', ')}] — deploy the runtime first, then the world (ADR-0001).`,
      );
    }
    return w;
  };
  // ── The launch-code handoff (docs/game-auth.md), read FIRST and scrubbed immediately.
  //
  // FIRST because `tuningFromHash()` below still tests `startsWith('#p=')`: opened as
  // `#launch=<code>&p=<base64>` the tuning override would be invisible until the launch key is
  // gone, and rewriting that predicate to tolerate a neighbour would be the more fragile fix.
  //
  // SCRUBBED because the code is a login-grade credential with a ~60 s life. It has done its job
  // the moment it is in this variable, and leaving it in the address bar puts it in the browser
  // history, in whatever the player copies out of the URL bar, and in any screen share. The
  // fragment is REBUILT rather than cleared, so a `#p=` tuning URL survives being opened with a
  // launch code beside it.
  const launch = parseLaunchFragment(location.hash);
  for (const w of launch.warnings) console.warn(`[axon] ${w}`);
  const scrubbedHash = hashWithoutLaunch(location.hash);
  if (scrubbedHash !== location.hash) {
    // replaceState, never assigning location.hash: the latter pushes a history entry, so Back
    // would restore the URL we just scrubbed. `pathname + search` keeps the rest of the URL and
    // drops a bare trailing '#'.
    history.replaceState(null, '', `${location.pathname}${location.search}${scrubbedHash}`);
  }

  const requested = new URLSearchParams(location.search).get('world') ?? 'sandbox-01';

  /*
   * ── ⭐ P1 · THE ASSET SERVICE IS CONSTRUCTED ONCE, HERE, AND ITS CATALOG RIDES THE `world` RUNG ─
   *
   * Two small JSON fetches that need each other for nothing: `world.json` describes the level,
   * `library/catalog/latest.json → <hash>.json` describes everything that belongs to no level. They
   * start on the SAME turn and are awaited together, so the library costs the boot one round trip
   * of latency rather than a rung of its own — which is also why there is no `catalog` rung: a
   * stage whose whole duration hides inside another stage's is a lie in the shape of a state
   * machine (`loadPhase.ts`'s own rule about `warming`).
   *
   * ⚠️ `loadCatalog()` NEVER THROWS (rule 6). A refusal leaves the service holding an EMPTY
   * catalog, every `acquire` resolves `failed('missing')`, the character keeps its capsule and the
   * companion keeps its hull. That is the whole degrade path and it is why this is not in the
   * `try` below: a dead library must never fall the world back to `sandbox-01`.
   *
   * ⚠️ AND THE ORIGIN IS READ FROM ONE NAMED BUILD KEY. `BUILD_ENV.DEV`, never `import.meta.env`
   * wholesale — the credential-leak rule in CLAUDE.md, and the reason `resolveLibraryOrigin` takes
   * `(search, dev, envLibraryOrigin)` as three arguments instead of an env object. The third is
   * `null` until the deployment plan's per-environment config file exists (plan §2.1: "until that
   * file exists, same-origin is the one answer"), so PRODUCTION IS SAME-ORIGIN and ignores the URL
   * entirely — a player cannot point this runtime at another host by typing a query string.
   */
  const libraryOrigin = resolveLibraryOrigin(location.search, BUILD_ENV.DEV, null);
  for (const w of libraryOrigin.warnings) console.warn(`[assets] ${w}`);
  /*
   * The accountant the SERVICE charges — and the same object `scene.getVramMB()`'s `ledgerMB` term
   * reads, handed down to `createScene`. One ledger, one census, no second opinion: before P1 the
   * character loader was the ONLY caller that ever charged it (`vramAccountant.ts:97-131` records
   * that this is why the census exists), and that charge is now the service's.
   */
  const vramLedger = new VramAccountant();
  const assets: AssetService = createAssetService({
    fetch: (url, init) => fetch(url, init as RequestInit),
    origin: libraryOrigin.origin,
    verify: libraryOrigin.verify,
    clock: () => performance.now(),
    vram: vramAccountantAdapter(vramLedger),
    // A THUNK, read at acquire time — `requires.classes` is gated against the registry exactly as
    // `world.requires` is, and reading it eagerly would freeze the answer before a vertical's
    // sub-table has been composed. Names only: `src/assets/**` may not import `src/entities/**`
    // (ADR-0001, `tests/entityBoundary.test.ts`), and this is how it does not have to.
    registryClasses: () => new Set(entityTypeNames()),
    // What this runtime can DECODE. `meshopt` is registered on the library's own GLTFLoader
    // (`assets/loaders/gltf.ts`, mirroring `staticMesh.ts:71-72`); `ktx2` lands with rendering R2
    // and its loader is a LAZY import behind this same gate.
    formats: new Set(['meshopt']),
  });
  const catalogP = assets.loadCatalog();

  /**
   * ⭐ §2.3's `lib:` ARM — the ONE resolver, injected. `world/assets.resolveAssetUrl` knows the
   * scheme and nothing else; the catalog, the content hash and the CDN layout live behind here.
   * An id the catalog does not carry warns ONCE and returns null, which is the placeholder the
   * decor entity already draws for an unknown world asset (rule 6 — a world may legally name a
   * library asset this catalog does not have, and that is a state, not a throw).
   */
  const libWarned = new Set<string>();
  const libraryUrl = (id: string): string | null => {
    const url = assets.fileUrl(id);
    if (url === null && !libWarned.has(id)) {
      libWarned.add(id);
      console.warn(
        `[assets] world asset 'lib:${id}' is not in the library catalog — drawing the placeholder. ` +
          `Publish it (npm run publish:library) or fix the uri.`,
      );
    }
    return url;
  };
  /**
   * ⭐ P1.5·B2 — THE LIBRARY'S OWN COLLISION, THROUGH THE SAME DOOR (plan §13.5).
   *
   * `AssetService.fileUrl(id, 'collision')` is a URL for a role the service never FETCHES itself
   * (`collision` is in no kind's `KIND_REQUIRED_ROLES`, so an `acquire()` transfers only the mesh
   * blob) — `static_mesh` owns that fetch, exactly as it owns the mesh's. `null` when the entry
   * carries no such role, which is every library mesh whose GLB authors no `UCX_/UBX_/USP_/UCP_`
   * node; the entity then walks the GLB, which is the shipped path and not a degraded one.
   *
   * ⚠️ DELIBERATELY SILENT WHERE `libraryUrl` WARNS. An absent collision role is the ORDINARY
   * case and the same verdict `absent` already means for a world with no cook; a warn-once here
   * would print a line on every library mesh in the catalog.
   */
  const libraryCollisionUrl = (id: string): string | null => assets.fileUrl(id, 'collision');

  let world: Awaited<ReturnType<typeof fetchWorld>>;
  /*
   * M3: the world's texture resolver, rebuilt lazily per world. It is a THUNK rather than a value
   * because `world` is REPLACED IN PLACE by editor loads (the same reason `resolveAssetUrl` is a
   * closure and not a precomputed map), and a resolver captured over the previous world would
   * serve the previous world's textures after a load with nothing to show it.
   */
  let textureResolverFor: { world: unknown; fn: ReturnType<typeof makeTextureResolver> } | null = null;
  const textureResolver = (): ReturnType<typeof makeTextureResolver> => {
    if (textureResolverFor === null || textureResolverFor.world !== world) {
      textureResolverFor = {
        world,
        // A texture asset's uri is a plain world-relative path — it never takes the `lib:` arm, so
        // the library resolver is deliberately not wired here and the null case cannot arise.
        fn: makeTextureResolver(world, (uri) => resolveWorldAssetUrl(world.worldId, uri, sessionAssets) ?? uri),
      };
    }
    return textureResolverFor.fn;
  };
  // Rung 2: `world` — one 2.8 KB fetch, a parse and the capability gate, beside the library
  // catalog's pointer + body. Nothing worth counting, and the ladder says so rather than
  // inventing a bar over it.
  toPhase('world');
  try {
    world = await fetchWorld(requested);
  } catch (err) {
    if (requested === 'sandbox-01') throw err;
    console.error(`[world] failed to load '${requested}' — falling back to sandbox-01:`, err);
    // L4.D (a): a SUBSTITUTION, not a failure. We opened something else and the plate says so once;
    // `blocked` is the other thing entirely — we opened nothing. Pushed immediately because the
    // next rung stamp will take the line back.
    loadNote = 'fellBack';
    pushScreen();
    world = await fetchWorld('sandbox-01');
  }
  /*
   * The library's half of the rung, joined here. It has been in flight for the whole of the world
   * fetch; awaiting it costs whatever is left. A refusal is DATA and is reported once — the boot
   * carries on either way, and `stats().catalog.assets === 0` is how a session with no library
   * tells itself apart from one whose catalog simply has nothing in it.
   */
  const catalogLoad = await catalogP;
  for (const w of catalogLoad.warnings) console.warn(`[assets] catalog: ${w}`);
  if (!catalogLoad.ok) {
    console.warn(
      `[assets] library catalog unavailable (${catalogLoad.error ?? 'unknown'}) — ` +
        `every library asset will draw its placeholder.`,
    );
  } else {
    console.info(
      `[assets] library catalog ${catalogLoad.hash ?? '(inline)'} — ${catalogLoad.assets} assets ` +
        `from ${libraryOrigin.origin === '' ? 'same-origin' : libraryOrigin.origin}` +
        `${libraryOrigin.verify ? ' (per-load sha256 verification ON)' : ''}`,
    );
  }

  const tuningFile = loadTuningFile(rawTuning);
  // World surface overrides merge over the content palette; the merged map is the ONLY one the
  // sim and scene ever see.
  const surfaces = mergeSurfaces(loadSurfaces(rawSurfaces), world.surfaces);
  // Mutable object the panel edits and the sim reads; derived values recomputed on change.
  const tuning = { ...getPreset(tuningFile, tuningFile.defaultPreset) };
  const fromHash = tuningFromHash();
  if (fromHash) Object.assign(tuning, fromHash);
  const derived = derive(tuning);
  const refreshDerived = () => Object.assign(derived, derive(tuning));

  /*
   * Rung 3: `engine` — Rapier's wasm, the whole of `createScene` (backend pick, device, sky,
   * lights, camera rig) and, on an exploration world, two more lazy chunks and a booted session.
   * THREE AWAITS WITH NO PROGRESS CHANNEL between them: `RapierCollisionService.create()` reports
   * nothing, `createScene` reports nothing, `loadExploration` reports nothing. The rung is opaque
   * and is declared opaque — inventing a fraction here would be the exact dishonesty the bar rule
   * exists to refuse. Note it already builds the level's OWN boxes and platforms — the FLOOR is
   * one of those boxes now, not an implicit plane this function added; only the MESH colliders
   * are still missing when it ends.
   */
  toPhase('engine');
  const physics = await RapierCollisionService.create({
    capsuleRadius: tuning.capsuleRadius,
    capsuleHeight: tuning.capsuleHeight,
  });
  // Colliders come from WORLD DATA (E0.5) — the same boxes/platforms the emitter wrote from
  // src/level/gym.ts, so the headless tests (which build physics from gym.ts directly) and the
  // playable level still share one source of truth via the drift guard.
  for (const b of world.boxes) {
    physics.addBox(
      { x: b.pos[0], y: b.pos[1], z: b.pos[2] },
      { x: b.size[0], y: b.size[1], z: b.size[2] },
      b.rotZDeg ?? 0,
      b.surface,
    );
  }
  for (const p of world.platforms) physics.addPlatform(p);

  const scene = await createScene(
    el,
    { radius: tuning.capsuleRadius, height: tuning.capsuleHeight },
    tuning,
    world,
    (from, to) => physics.cameraProbe(from, to),
    surfaces,
    // P1: the character is a LIBRARY asset now (`player-body-blue-001` + its clips, boot lane), so
    // presentation is handed the service rather than a base URL. The accountant travels with it
    // because the service is what charges it — `scene.ts` must not open a second ledger.
    { assets, vram: vramLedger },
    // The resolution controller's UI freeze (hard rule 9's predicate, injected — presentation
    // does not ask the input layer questions on its own). A stage or cutscene layer being up is
    // the worst possible moment for a resolution pop.
    uiHasFocus,
    skinFromSearch(location.search),
  );

  // Entity layer (E0, ADR-0001): placements → registry-validated defs → lazy-chunked instances.
  // Entity events buffer here and join the frame's bus BEFORE the SFX/VFX/rumble subscribers run.
  const pendingEntityEvents: FeedbackEvent[] = [];
  // Session asset store (A4): manifest uri → object URL for editor-imported files. Mount owns
  // the map so decor entities resolve session blobs in Play mode too; the editor writes it.
  const sessionAssets = new Map<string, string>();
  /*
   * ── ⭐ P1.5·C · THE LIBRARY'S TWO DOORS ONTO THE ENTITY HOST (plan §13.6) ─────────────────────
   *
   * `src/assets/**` may not import `src/entities/**` (ADR-0001), so the service RESOLVES a library
   * entity and the app layer SPAWNS it. `src/app/librarySpawn.ts` is the one place the two meet;
   * mount wires two one-way callbacks and neither side learns the other's module graph — the same
   * seam shape `registryClasses` above already uses in the opposite direction.
   *
   * The `let` is the knot: the host needs the resolver and the resolver needs the host. It is
   * assigned on the very next statement and read only from callbacks that run later, so there is
   * no temporal-dead-zone window — the `tickCount` discipline, one subsystem over.
   */
  let libraryEntities: LibraryEntities | null = null;
  /*
   * ⭐ THE STATIC BATCHER (`render/batching/staticBatcher.ts`) — one `InstancedMesh` per
   * (asset, primitive, cell, winding) instead of a drawn mesh per placement.
   *
   * ⚠️ OFF WHILE THE EDITOR IS OPEN, and that is a capability gap rather than a preference: editor
   * selection maps `object3d → placement index`, and an `InstancedMesh` replaces that with
   * `(batch, instanceId)`. `select.ts` can already read an `instanceId` — it does exactly that for
   * the gym's boxes — but nothing maps one back to a placement yet. So the editor draws a mesh per
   * placement (today's behaviour, unchanged) and PLAY takes the batched path. The F2 transition
   * already respawns every entity, so both sides rebuild cleanly.
   *
   * Declared as a mutable flag rather than reading `editorActive` because that binding is declared
   * several hundred lines below this closure and entities may spawn before it is initialised.
   */
  /*
   * `?cell=<m>` — the DEV override on the batcher's spatial grain, and it exists because
   * `DEFAULT_CELL_M`'s own comment asks for exactly this: *"the profile chooses it, not this
   * comment"*. `0` / `inf` / `none` mean no cells at all, which is the honest baseline the constant
   * is measured against. Anything unparseable takes the authored value and warns once (rule 6).
   */
  const cellParam = new URLSearchParams(location.search).get('cell');
  const minCullParam = new URLSearchParams(location.search).get('minculltris');
  const staticBatcher = createStaticBatcher(
    scene.entityRoot,
    resolveCellM(cellParam),
    resolveMinCullTris(minCullParam),
  );
  /*
   * ── ⭐ `?batch=0` — THE BATCHER'S OFF SWITCH, AND IT IS A DIAGNOSTIC BEFORE IT IS A LEVER ───────
   *
   * The batcher had no off switch, so there was no way to ask *"is this artefact the batcher?"* of
   * a running frame — and the owner is reporting black faces on surfaces that survive `?gi=off`,
   * which is precisely that question. With batching off every placement draws its own mesh through
   * `composeStaticSubtree`, the path the EDITOR already uses and the path that has never shown the
   * artefact, so the comparison is one variable.
   *
   * ⚠️ The specific hypothesis it exists to test, which is UNVERIFIED and must not be reported as a
   * finding until this arm says so: three transforms an instanced normal by the inverse-transpose
   * (`Instance.js:236`), and a mirrored batch then renders with `side = BackSide`, which makes the
   * face back-facing and negates the normal AGAIN. A double flip would light 627 of Mars's mirrored
   * placements from behind — which looks exactly like a black face.
   *
   * ⛔ IT IS NOT A PERFORMANCE OPTION. Off means ~5,563 draws and a frame nobody would ship; the
   * whole point is that the PICTURE is comparable while the cost is not.
   */
  const batchParam = new URLSearchParams(location.search).get('batch');
  let batchingEnabled = batchParam !== '0';
  if (batchParam === '0') {
    console.warn(
      '[render] ?batch=0 — the static batcher is OFF. Every placement draws its own mesh: this is a ' +
        'DIAGNOSTIC arm for comparing the picture, never a performance setting.',
    );
  }
  const host = new EntityHost({
    root: scene.entityRoot,
    emit: (e) => pendingEntityEvents.push(e),
    burst: (pos, count) => scene.burstAt(pos, count),
    // `world` is replaced IN PLACE by editor loads (replaceWorld), so this closure stays true.
    // P1: the fourth argument is the LIBRARY arm (`lib:<id>` → the AssetService's own file URL).
    // Session blob first, then `lib:`, then the world dir — one resolver, three namespaces, and
    // the entity class learns none of it.
    resolveAssetUrl: (assetId) => meshAssetUrl(world, assetId, sessionAssets, libraryUrl),
    // B3: the host applies each class's `EntityMeta.shadow` through the ONE applier
    // (`render/lights/shadowFlags.ts`); entities never write `castShadow`/`receiveShadow`.
    applyShadowFlags: (root, flags) => {
      applyShadowFlags(root, flags);
    },
    // The matrix policy's other half: the class says whether it ever moves, the scene says what
    // that means. See `EntityMeta.mobility` — it was 20.4 % of a 45 ms CPU frame.
    applyMobility: (root, mobility) => {
      scene.applyMobility(root, mobility);
    },
    batchStatic: (assetId, assetScene, pose) =>
      batchingEnabled && staticBatcher.add(assetId, assetScene, pose),
    // S5: the manifest's collision declaration for a mesh asset — one input to the ONE resolver,
    // which the entity runs beside its own placement field (`world/collision.ts`).
    assetCollisionMode: (assetId) => {
      const a = world.assets.find((x) => x.id === assetId);
      return a !== undefined && a.kind === 'mesh' ? a.collision : undefined;
    },
    /*
     * ⭐ M3: the world MATERIALS a mesh asset's slots bind to, resolved here and handed over ready.
     *
     * The entity gets live materials, not ids, for the same reason it gets a URL and not a
     * manifest: `src/render/materials` stays off the entity chunk's import graph, and the entity
     * has no business knowing that `materialFor` memoizes per (world, id) or that textures resolve
     * through a slot-aware resolver. It asks "what goes on this asset" and gets an answer.
     *
     * `materialFor` is memoized per (world, id), so 29 materials are built ONCE for the whole
     * level however many of the 5,564 placements ask — which is what keeps them sharing one
     * program each (hard rule 7).
     */
    assetMaterials: (assetId) => {
      const a = world.assets.find((x) => x.id === assetId);
      if (a === undefined || a.kind !== 'mesh' || a.materials === undefined) return null;
      return a.materials.map((id) => (id === null ? null : materialFor(world, id, textureResolver())));
    },
    // L2: the cooked sidecar's URL, by CONVENTION (`collision/<assetId>.col`) — never a manifest
    // field, because a new key there moves `worldHash` and a moved `worldHash` refuses the world's
    // baked GI atlas. Same closure discipline as `resolveAssetUrl`: `world` is replaced in place by
    // editor loads, and the session store is consulted so a freshly imported blob asks for nothing.
    // P1.5·B2: the fourth argument is the LIBRARY arm — a `lib:` mesh reads its OWN `collision`
    // role instead of a per-world path, which is the whole of "a library mesh carries its own
    // collision" (plan §13.5). One seam, two namespaces; the entity learns neither.
    resolveCollisionUrl: (assetId) => collisionSidecarUrl(world, assetId, sessionAssets, libraryCollisionUrl),
    // P1: the ONE asset service, for classes whose assets belong to no world (the companion's
    // body, maps and clips today; cosmetics and gadgets at P2/P3). Handed as the object, not as a
    // factory: there is exactly one per session and a class that made its own would be a second
    // ledger, a second scheduler and a second answer to "what is resident".
    assets,
  },
  // The host's `lib:` arm: a placement whose type is `lib:<id>` becomes ONE MORE ROW in the prefab
  // table `resolveEntityDef` already folds, so ADR-0002's precedence stays the one merge rule.
  { prefab: (id) => libraryEntities?.prefab(id) ?? null });

  libraryEntities = createLibraryEntities({
    assets,
    host,
    // §4.2's placeholder policy for the `entity` row (`summonPlaceholder.ts` says why it is a cage
    // and not a magenta box). Injected so `librarySpawn.ts` stays free of `three` and headless.
    placeholder: createSummonPlaceholder(scene.entityRoot),
  });
  // S5: the `collision` show flag draws what the PHYSICS world holds — presentation never imports
  // physics, so mount hands the scene a window onto it once, here.
  scene.setCollisionOverlaySource(() => physics.entityCollisionShapes());

  /*
   * ── ⭐ S5 · STATIC-MESH COLLISION IS BOUND BY THE HOST, AND POPULATION HAPPENS IN ONE PLACE ────
   *
   * An entity PUBLISHES `EntityInstance.collision` by the time its `ready` resolves (the
   * `applyShadowFlags` direction, reversed: the class declares, the host acts); `bindCollision`
   * waits for that `ready`, reads it, and hands it to Rapier keyed by PLACEMENT INDEX with the pose
   * taken from the live `world.entities[index]` (the same def the editor mutates in place).
   *
   * `spawnEntities` is the ONE site that calls `host.loadAll*` (B3, `tests/mountSpawn.test.ts`):
   * the boot path and the editor's Play transition (`exitEditor`: `resetLevel` → `dispose` →
   * repopulate) both go through it, because a binding registered only at boot is the seventeenth
   * "compiled, passed tests, did nothing" — Sponza would be solid until the first F2 → Play and
   * hollow forever after, with every screenshot identical. The editor binds its OWN spawns through
   * `EditorCtx.collision.bind` (same function), so a placed or respawned mesh is solid in Play.
   *
   * `boundGen` is a per-index generation: `ready` is asynchronous and a rebind can be asked for
   * while an older one is still loading — the older resolution must not overwrite the newer.
   */
  const boundGen = new Map<number, number>();
  /*
   * ⚠️ THE GENERATION IS GLOBALLY MONOTONIC, NOT PER INDEX, AND THAT IS THE WHOLE GUARD.
   *
   * It used to be `(boundGen.get(index) ?? 0) + 1`, which is correct until something calls
   * `boundGen.clear()` — and `spawnEntities()` and the editor's `collision.clear` both do. After a
   * clear the next bind for index i is generation 1 again, which is EXACTLY the number an
   * in-flight older bind for that index is still holding, so the stale handler's `!==` check passes
   * and it writes over the fresh binding. Concretely: boot binds Sponza while its GLB is still
   * loading; F2 within those seconds respawns and re-binds at the same index; the first (disposed)
   * instance's `ready` then resolves, passes the guard and calls `setEntityCollision(i, null, …)`,
   * whose first act is `removeEntityCollision(i)`. Sponza goes hollow with no error anywhere. The
   * same shape hits a delete, where indices have shifted, so an old instance's shapes bind at
   * another placement's pose. A monotonic counter cannot be reset into a collision: a clear leaves
   * `get(index)` undefined, and `undefined !== <any past gen>` refuses every stale handler.
   */
  let bindSeq = 0;
  const assetIdOf = (def: unknown): string =>
    typeof def === 'object' && def !== null && typeof (def as { asset?: unknown }).asset === 'string'
      ? (def as { asset: string }).asset
      : '';
  /*
   * ⭐ L3.B · SETTLEMENT — "ITS LOAD FINISHED" IS A DIFFERENT FACT FROM "IT BOUND A COLLIDER".
   *
   * Placement indices whose load has come to rest this populate cycle, whatever it came to rest
   * ON. `residency()` folds it against the bound set to separate STILL COMING from CAME BACK WITH
   * NOTHING, and that separation is worth twenty seconds: `staticMesh` resolves `ready` after a
   * 404 (it draws a placeholder — hard rule 6), so a dead asset used to leave `resident` false
   * until the deadline fired. One missing crate, twenty seconds of a frozen world.
   */
  const settledIdx = new Set<number>();
  /**
   * ── ⭐ THE BIND QUEUE — WHY THE PER-PLACEMENT BIND IS BUDGETED AND THE PER-ASSET WALK WAS NOT ────
   *
   * `extractCollision` has been budgeted since L1.0, and that budget covers the per-ASSET walk: 177
   * of them on `mars-hangar-01`, each cached and shared. `physics.setEntityCollision` is the per-
   * PLACEMENT half — 5,563 of them — and it had no budget, no yield and no await anywhere in its
   * body. Measured against the real `world.json` and real Rapier 0.20:
   *
   *     createCollider(trimesh) × 5,827   32,562 ms      QBVH build + JS↔WASM marshal per collider
   *     transformShapes (JS)               3,448 ms      6.2 M vertex rotations
   *     createRigidBody × 5,563              159 ms
   *
   * ⚠️ AND IT ARRIVED AS A HANDFUL OF ENORMOUS BLOCKS RATHER THAN AS A SLOW TRICKLE, which is the
   * part that made it a frame-rate bug instead of a long load. Every placement of an asset derives
   * `ready` from the SAME cached `loadSharedAsset(url)` promise, so all of that asset's `.then`
   * callbacks run in ONE microtask drain — and microtasks never yield, so no rAF fires and NOTHING
   * RENDERS until the drain ends. The three worst on this level:
   *
   *     12.51 s  sm-l-supportstrut-panelled ×402
   *      9.24 s  sm-l-wall                  ×437
   *      8.84 s  sm-l-lean-wall             ×411
   *
   * Those blocks ARE the ~0.05 fps the owner reported. The work is the same size either way; what
   * changes here is that it stops being one turn.
   *
   * ⚠️ THE DRAIN IS SCHEDULED ON A MICROTASK, AND STARTING IT SYNCHRONOUSLY WOULD DO NOTHING AT ALL.
   * If the first `.then` callback drained immediately it would find a queue of ONE, bind it, and
   * finish — and so would each of the other 436, giving 437 synchronous drains of one item and not a
   * single yield. Deferring by one microtask lets every push in the current drain land first, so the
   * budget has a queue to spend itself against. A lone editor spawn still binds inside its own turn.
   */
  const bindQueue: { index: number; gen: number; inst: EntityInstance }[] = [];
  let bindDraining = false;
  let bindScheduled = false;
  const drainBinds = async (): Promise<void> => {
    if (bindDraining) return;
    bindDraining = true;
    try {
      while (bindQueue.length > 0) {
        /*
         * ⚠️ THE BUDGET IS READ PER SLICE, NOT ONCE — it must follow the gate, which opens partway
         * through this very drain (the 20 s deadline fires with thousands still queued). Behind the
         * plate, finishing is what matters and 100 ms turns are ~10 fps of a surface nobody is
         * aiming a camera at; the moment the player is in the world, a 100 ms turn is a lurch.
         */
        const budgetMs = worldResident ? YIELD_BUDGET_MS : LOADING_BUDGET_MS;
        const t0 = performance.now();
        do {
          const job = bindQueue.shift()!;
          // Re-checked HERE and not only at enqueue: a newer bind for this index can be issued while
          // this job sits in the queue, which is a window that did not exist when the bind was
          // synchronous. `boundGen` is globally monotonic, so a stale job can never match.
          if (boundGen.get(job.index) !== job.gen) continue;
          settledIdx.add(job.index);
          physics.setEntityCollision(
            job.index,
            job.inst.collision ?? null,
            placementPose(world.entities[job.index]),
            assetIdOf(world.entities[job.index]),
          );
        } while (bindQueue.length > 0 && performance.now() - t0 < budgetMs);
        // Both were per-bind and are now per-SLICE: the overlay is a debug view that only has to be
        // right by the time a frame draws, and the gate coalesces on its own microtask anyway.
        scene.syncDebugView();
        refreshResidency(); // the bind is the fact the gate waits on — see `worldResident`
        /*
         * ⚠️ `yieldTask`, NEVER `yieldFrame` — see that module's measurement. Waiting for a paint
         * after every slice costs a whole load frame per slice, and over thousands of slices that
         * is most of the load spent asleep. A macrotask lets the browser paint if it wants to and
         * does not sit and wait for it.
         */
        if (bindQueue.length > 0) await yieldTask();
      }
    } finally {
      bindDraining = false;
    }
  };
  const scheduleBindDrain = (): void => {
    if (bindScheduled || bindDraining) return;
    bindScheduled = true;
    queueMicrotask(() => {
      bindScheduled = false;
      void drainBinds();
    });
  };
  const bindCollision = (index: number, inst: EntityInstance | null): void => {
    const gen = ++bindSeq;
    boundGen.set(index, gen);
    // A rebind un-settles: the answer is in flight again until this generation's `ready` lands.
    settledIdx.delete(index);
    physics.removeEntityCollision(index);
    if (inst === null) {
      // No instance at all (a def the registry refused, or an editor delete). There is nothing to
      // wait for, so it settles NOW — otherwise a world with one bad row holds the gate for the
      // full deadline while nothing anywhere is still loading.
      settledIdx.add(index);
      scene.syncDebugView();
      refreshResidency();
      return;
    }
    void (inst.ready ?? Promise.resolve()).then(
      () => {
        if (boundGen.get(index) !== gen) return; // a newer bind for this index superseded us
        /*
         * ⚠️ `settledIdx.add` MOVED INTO THE DRAIN, deliberately. It used to be stamped the instant
         * `ready` resolved, which would now mark a placement settled while its colliders are still
         * queued — and `residency()` folds `settled` against `bound` to tell CAME BACK WITH NOTHING
         * apart from STILL COMING. Stamping it here would make every queued placement read as a hole
         * in the level for as long as it waits. Queued is still coming, and that is the truth.
         */
        bindQueue.push({ index, gen, inst });
        scheduleBindDrain();
      },
      () => {}, // `ready` never rejects by contract; belt and braces (rule 6)
    );
  };
  /*
   * ── ⭐ THE WORLD-READINESS GATE (S7) — THE SIM WAITS FOR PHYSICS, NOT FOR THE LOADER ───────────
   *
   * `src/world/readiness.ts` carries the argument. The short version: `loadAllIndexed` returns on
   * CONSTRUCTION, the GLB arrives in a `.then()`, and `bindCollision` binds in another one — so
   * without this the fixed-tick accumulator ran against an empty physics world for the 2.9 s
   * Sponza's 103 trimeshes take to extract and bind, and the player fell through the floor.
   *
   * It gates the SIM, never the FRAME: rendering continues (a loading frame is still a frame, and
   * the shader warm-up wants them), the platforms stay posed at t=0, and only the tick loop and
   * input consumption wait. That is the same shape `bakeHoldsFrame()` uses, inverted.
   */
  let worldResident = false;
  let residentWarned = false;
  /** P1 · has the frame loop already reacted to the library's boot lane going resident? */
  let libraryGateSeen = false;
  /** L3.D · why the gate opened. `''` until it has; never re-armed silently. */
  let gateOpened: '' | 'resident' | 'partial' | 'deadline' = '';
  /**
   * ⭐ THE REQUIRED SET IS DERIVED ONCE PER POPULATE CYCLE, NOT ONCE PER BIND.
   *
   * `requiredResidents` is a pure fold over `(entities, prefabs, assets)` that builds a Map of every
   * asset and resolves every prefab across every row — 0.56 ms on `mars-hangar-01`'s 5,564. Nothing
   * mutates a placement between `spawnEntities()` and the gate opening, so calling it per bind
   * bought 5,563 identical answers. `spawnEntities()` is L3's ONE re-entry and is the ONE place
   * allowed to clear this, for exactly the reason it is the one place allowed to lower
   * `worldResident`: it is the one thing that changes what the answer would be.
   */
  let requiredCache: ReturnType<typeof requiredResidents> | null = null;
  const requiredNow = (): ReturnType<typeof requiredResidents> =>
    (requiredCache ??= requiredResidents(world.entities, world.prefabs, world.assets));
  /**
   * The last fold. Cleared by every bind — a bind is the only thing that moves the answer — so the
   * `pushScreen()` at the top of a flush and the flush's own read are ONE fold and not two, which
   * is what they were.
   */
  let residencyCache: ReturnType<typeof residency> | null = null;
  const readResidency = (): ReturnType<typeof residency> =>
    (residencyCache ??= residency(
      requiredNow(),
      // Not `entityCollisionInfo().filter().map()`: that built a copy of the whole collision report,
      // twice per bind, to produce a list of integers. See `boundEntityIndices`.
      physics.boundEntityIndices(),
      // L3.B: the third argument is what separates "still coming" from "came back with nothing".
      settledIdx,
    ));
  /**
   * ⭐ P1 · THE LIBRARY'S HALF OF THE GATE (ADR-0003 §4.3). `AssetService.bootGate()` is
   * `assets/core/readiness.bootGate` over the live handles — boot lane ONLY, a failed handle
   * counted `broken` rather than `pending` — projected into the four integers `world/readiness.ts`
   * folds. Gameplay and prefetch never appear here: that is the entire difference between a boot
   * request and a mid-play summon, and it is why the companion (gameplay lane) can arrive at any
   * time without joining this ladder.
   */
  const readLibraryResidency = (): ReturnType<typeof libraryResidency> =>
    libraryResidency(assets.bootGate());
  /**
   * Re-read after every bind. Latched true so a later unbind (an editor delete) cannot re-freeze —
   * and since L3.C that latch means "SINCE THE LAST SPAWN", not "forever": `spawnEntities()` is the
   * one place allowed to lower it, because it is the one place that empties the collision set.
   */
  // L4: the hole opened at the top of mount() is filled the moment the fold exists. From here the
  // plate draws `residency()`'s own numbers, and `determinate` stays a CONDITIONAL on the live
  // count — never a constant, so a future world with 40 crates flips the bar on with no edit.
  loadCounts = () => {
    const r = readResidency();
    return { determinate: determinate(r.required.length), bound: r.bound, required: r.required.length };
  };
  /**
   * ── ⭐ ONE FOLD PER MICROTASK DRAIN, NOT ONE PER BIND — AND THAT IS WHY IT IS A MICROTASK ───────
   *
   * Every placement of an asset derives `ready` from the SAME cached `loadSharedAsset(url)` promise,
   * so when it resolves, all of that asset's placements' `.then` callbacks run in ONE microtask
   * drain. Microtasks do not yield, so no rAF fires and NO FRAME RENDERS until the drain completes:
   * an asset with 437 placements produces one unyielded block, not 437 small ones. Measured on
   * `mars-hangar-01`, the three worst were 12.51 s, 9.24 s and 8.84 s — those blocks ARE the
   * "0.05 fps" frames, and this fold was ~12 s of the total, run TWICE per bind.
   *
   * A `queueMicrotask` scheduled from inside a drain lands at the END of that drain — microtasks are
   * FIFO and the 437 callbacks are already queued ahead of it — so one flag plus one microtask
   * collapses 437 folds into one, at the earliest moment the answer can have settled.
   *
   * ⚠️ IT IS DELIBERATELY NOT `requestAnimationFrame`. rAF is throttled to zero in a hidden tab, and
   * this gate is what starts the sim; scheduling the gate on a clock the browser is entitled to stop
   * would make a backgrounded load hang until the 20 s deadline rescued it. A microtask always runs.
   */
  let residencyDirty = false;
  const refreshResidency = (): void => {
    // A bind is the only thing that changes the answer, so it is the only thing that invalidates.
    residencyCache = null;
    if (worldResident || residencyDirty) return;
    residencyDirty = true;
    queueMicrotask(flushResidency);
  };
  function flushResidency(): void {
    residencyDirty = false;
    if (worldResident) return;
    /*
     * L4: the `streaming` counter moves HERE, not on the rung stamps. Rung transitions are the
     * only other push site, and there is exactly one of them left after `streaming` — so a world
     * that DID clear `BAR_MIN_STEPS` would draw a bar that never moved. Every bind lands here;
     * this is the only place that is true.
     *
     * ⚠️ AND ON `mars-hangar-01` IT REALLY DOES REPAINT — `determinate` is TRUE on this world for
     * the first time (`BAR_MIN_STEPS` is 4 and `required` is 5,563), so CLAUDE.md's "determinate is
     * false on all six worlds, so the `steps` arm is correct, tested and unpainted" is STALE. That
     * path had never run before this level; coalescing means it now runs once per drain rather than
     * 11,126 times.
     */
    pushScreen();
    const r = readResidency();
    const lib = readLibraryResidency();
    if (!worldPlayable(r, lib)) return;
    worldResident = true;
    // `partial` and `resident` are both open gates and must not read the same in a report: one is
    // a healthy boot, the other is a level with a known hole in it that chose not to wait.
    gateOpened = r.broken.length > 0 ? 'partial' : 'resident';
    // L4.D (a): a level with a known hole in it says so, for the length of the fade. `resident`
    // says nothing — a healthy boot needs no sentence.
    if (r.broken.length > 0) loadNote = 'partial';
    toPhase('playing');
    const libLine = describeLibraryResidency(lib);
    console.info(`[world] playable — ${describeResidency(r)}${libLine === '' ? '' : ` · ${libLine}`}`);
  }
  /*
   * ⚠️ THE DEADLINE IS NOT OPTIONAL. A gate with no way to give up turns one slow asset — or a
   * background tab whose rAF never fires — into a boot that never starts. Rule 6: warn once, then
   * proceed. Better a level that begins with a hole in the floor and says so than a black screen.
   */
  const RESIDENCY_DEADLINE_MS = 20_000;
  /** The live deadline. Restarted by `spawnEntities()`, cleared by `unmount` — never two at once. */
  let residencyTimer: ReturnType<typeof setTimeout> | null = null;
  const openGateAnyway = (): void => {
    if (worldResident) return;
    worldResident = true;
    gateOpened = 'deadline';
    // L4.D (b): the deadline going from a console line to a sentence a ten-year-old reads is the
    // right pressure. It is never a dead end — the gate opens, the plate leaves, and the player
    // lands in a level with a hole in the floor rather than in front of a screen that lied.
    loadNote = 'slow';
    toPhase('playing');
    if (!residentWarned) {
      residentWarned = true;
      const r = readResidency();
      const stuck = assets.bootGate().pending;
      console.warn(
        `[world] starting WITHOUT full collision after ${RESIDENCY_DEADLINE_MS / 1000}s — ` +
          `${describeResidency(r)}. Missing placements: ${r.missing.join(', ') || 'none'}.` +
          // P1: the library can hold this gate too now, so the deadline must be able to name it.
          // A boot that timed out on one clip and one on a wedged trimesh read identically before.
          (stuck.length > 0 ? ` Library boot assets still coming: ${stuck.join(', ')}.` : ''),
      );
    }
  };
  /*
   * ── ⭐ L3.C · THE GATE RE-ARMS HERE, AND ONLY HERE ─────────────────────────────────────────────
   *
   * `spawnEntities` is already the ONE `host.loadAll*` site (B3, `tests/mountSpawn.test.ts`), which
   * makes it the exact and only place that may re-open the gate: it is what empties the entity
   * collision set, so it is what must wait for it to fill again.
   *
   * ⚠️ AND WITHOUT THIS, F2 → PLAY RAN THE SIM AGAINST AN EMPTIED COLLISION SET. `worldResident`
   * was latched forever (`refreshResidency` opens `if (worldResident) return`), so on Sponza the
   * Play transition did `resetLevel` → `dispose` → `spawnEntities` and then simulated immediately:
   * `resetLevel` re-added the forced GROUND PLANE, so the player SANK ~1 m to y = 0 and walked
   * through every wall for the seconds the trimeshes take to rebind. The boot path was protected;
   * the Play path was not, and every screenshot of both looks identical.
   *
   * ⚠️ HISTORY WORTH KEEPING, AND NOW ONLY HISTORY: the plane is deleted (it was the level's floor
   * on no world's authority, and on Sponza it stood 0.99 m ABOVE the marble — the same 1 m this
   * paragraph measures, from the other side). `resetLevel` re-adds only what the world authored,
   * so the SINK is gone; the GATE is not, because the trimesh rebind it guards is unchanged.
   *
   * `bindCollision` and the editor's `EditorCtx.collision.clear` deliberately do NOT come here —
   * that is precisely what the latch was protecting (an editor delete must not re-freeze a live
   * session), and it stays protected. `enterEditor` raises nothing because it never populates the
   * host, and the editor's own `respawnAllEntities` goes through `collision.clear` + per-instance
   * `bind` rather than through this function: an in-editor respawn is a creator action inside an
   * ALREADY frozen sim, not a level load.
   */
  const spawnEntities = async (): Promise<void> => {
    worldResident = false;
    residentWarned = false;
    // The library edge re-arms with the gate: a Play transition freezes the sim exactly as boot
    // does, and a boot handle that is still coming must hold it there for the same reason.
    libraryGateSeen = false;
    gateOpened = '';
    // A second load starts with a clean sheet: the previous cycle's "some parts are missing" must
    // not be the first thing a Play transition puts on screen.
    loadNote = '';
    settledIdx.clear();
    // The required set and the last fold are BOTH facts about the cycle that just ended: this is the
    // one place the world's placements can have changed under them (a Play transition re-spawns from
    // an edited world), and it is the same reason this is the one place `worldResident` may fall.
    requiredCache = null;
    residencyCache = null;
    // Jobs from the cycle that just ended. `boundGen.clear()` already makes every one of them fail
    // the generation check, so this is not a correctness fence — it is refusing to carry thousands
    // of dead entries into the next load and pay a shift() for each.
    bindQueue.length = 0;
    // The ONE re-entry into the ladder, through `announce` like every other transition so the
    // second load prints exactly what the first one did. The phase is read BEFORE the restart —
    // that is the whole point of the pair, and inlining it would depend on argument order.
    const wasPhase = load.phase;
    announce(wasPhase, load.restart(nowMs()));
    // The plate covers the SECOND load for the same reason it covers the first: the gate below
    // freezes the sim until the colliders are back. Boot already has one up; a Play transition
    // does not, because the boot plate released.
    showScreen();
    // One deadline per arm. A stale timer from the previous cycle would fire mid-load and open the
    // gate on a world that was loading perfectly well.
    if (residencyTimer !== null) clearTimeout(residencyTimer);
    // The run-up is over; the residency deadline takes the boot watchdog's place. Never two live.
    if (bootTimer !== null) clearTimeout(bootTimer);
    bootTimer = null;
    residencyTimer = setTimeout(openGateAnyway, RESIDENCY_DEADLINE_MS);
    physics.clearEntityCollision();
    boundGen.clear();
    /*
     * ⭐ THE PLACEMENT'S LIFETIME, TAKEN BEFORE THE PLACEMENTS ARE (plan §13.6). One handle per
     * distinct `lib:` id this world places, held while the world is loaded and swapped wholesale
     * on the next populate cycle — the prefab's declared `data` assets are its dependency closure,
     * which is what makes "declared at the entity level, so dependencies are handled
     * automatically" true for a PLACED entity as well as for a summoned one. The GAMEPLAY lane, so
     * it never gates boot: a sim whose skin is late draws its stock skin, which is a picture and
     * not a fault, and holding the loading plate for it would spend a player's boot on a
     * difference they cannot see.
     */
    libraryEntities?.retain(world.entities);
    const insts = await host.loadAllIndexed(world.entities, world.prefabs);
    // Rung 5: every placement is CONSTRUCTED; what remains is bytes arriving and colliders binding.
    toPhase('streaming');
    insts.forEach((inst, i) => bindCollision(i, inst));
    /*
     * The gate's other end, and it belongs INSIDE the one populate site rather than beside the boot
     * call: a world with nothing required (no solid geometry at all) has no bind coming to trigger
     * `refreshResidency`, so it must be asked once here — on the boot path AND on the Play path.
     */
    refreshResidency();
  };
  // Deterministic sim clock for the moving platforms AND the exploration hooks' timeMs. Declared
  // BEFORE the exploration loader: the session boots synchronously inside loadAll's onAllSpawned
  // flush, so hooks.timeMs() runs before the render loop exists — a later `let` here is a
  // temporal-dead-zone crash (the exact bug that killed the first X1 playtest).
  let tickCount = 0;
  // The time the RENDERER poses platforms at, published for the companion hooks (Axon plan §3.3a).
  // Written once per frame in the loop below; declared HERE for the tickCount reason — the hooks
  // object closes over it and the vertical boots inside loadAll, before the loop exists.
  let renderTimeS = 0;
  // Device sources, declared BEFORE the exploration loader for the SAME temporal-dead-zone reason
  // as tickCount: the hooks object closes over `gamepad` (padInteractHeld).
  const keyboard = new KeyboardSource();
  const mouse = new MouseLookSource();
  const gamepad = new GamepadSource();
  // ── Exploration vertical (X1d, ADR-0001): the world's `requires` doubles as the load trigger —
  // non-exploration worlds never download the chunk. Loaded BEFORE loadAll so the orchestrator
  // owns the world-services booter bracket (reset → populate → start; worldServices contract).
  // Mount touches the vertical ONLY through loader.ts + these hooks.
  let exploration: ExplorationHandle | null = null;
  let explorationTick: ((timeMs: number) => void) | null = null;
  if (explorationRequired(world.requires)) {
    const { loadExploration } = await import('../exploration/loader');
    exploration = await loadExploration({
      world,
      host,
      search: location.search,
      // Already parsed, validated and scrubbed out of the URL at the top of mount. Null on every
      // dev session and every world opened without a handoff, which is the normal case.
      launchCode: launch.code,
      playerPos: () => curr.pos,
      // The existing respawn mechanics, reused: motion-state reset (fall-respawn's exact move);
      // null target ⇒ the resolved player_start spawn, same as falling off the world.
      respawn: (target) => {
        curr = createMotionState(target ? v3(...target.pos) : spawnPos(), tuning);
        prev = curr;
      },
      timeMs: () => tickCount * FIXED_DT * 1000,
      // The vertical's interact edge reads THIS frame's already-polled pad instead of calling
      // navigator.getGamepads() itself (it was polling a second time, every frame, forever).
      padInteractHeld: () => gamepad.interactHeld(),
      // ── COMPANION HOOKS (Axon plan §3.3a). Projections only — the vertical never sees `scene`,
      // `physics` or the live MotionState, and `camera()` is READ-ONLY: Axon is never an input to
      // `physics.cameraProbe` (plan §3.2). All four close over `curr`/`renderFeet`/`scene`, which
      // are declared BELOW this block; that is safe for the same reason `playerPos` is — they are
      // called from the render loop, long after the temporal dead zone has closed.
      camera: () => {
        const m = scene.camera.matrixWorld.elements;
        return {
          pos: { x: m[12] ?? 0, y: m[13] ?? 0, z: m[14] ?? 0 },
          // three's camera looks down its LOCAL −Z.
          fwd: { x: -(m[8] ?? 0), y: -(m[9] ?? 0), z: -(m[10] ?? 0) },
          right: { x: m[0] ?? 0, y: m[1] ?? 0, z: m[2] ?? 0 },
          up: { x: m[4] ?? 0, y: m[5] ?? 0, z: m[6] ?? 0 },
          fovY: (scene.camera.fov * Math.PI) / 180,
          aspect: scene.camera.aspect,
        };
      },
      playerVel: () => curr.vel,
      playerRenderPos: () => renderFeet,
      playerTraversal: () => ({
        mode: curr.mode,
        grounded: curr.grounded,
        teetering: curr.teetering,
        msSinceTakeoff: curr.msSinceTakeoff,
        // The CAMERA RIG's yaw: the character orients to velocity, so the rig is what the player
        // actually steers and therefore what "facing" means for a follow arc.
        yawDeg: (scene.rig.yaw * 180) / Math.PI,
      }),
      renderTimeS: () => renderTimeS,
      onFrame: (cb) => {
        explorationTick = cb;
        return () => {
          if (explorationTick === cb) explorationTick = null;
        };
      },
    });
  }
  /*
   * ── Main-menu vertical (main-menu plan §4, ADR-0001): the SAME shape one line up — the world's
   * `requires` doubles as the load trigger, so a world that places no `menu_stage` never downloads
   * a byte of the menu. Mount touches it ONLY through loader.ts and these hooks; no world id
   * appears here.
   *
   * ⭐ AND THIS IS THE **BUILD** HALF ONLY. It runs here, above `spawnEntities()`, deliberately —
   * the chunk, the data port and the surface's DOM are all in flight while the level builds. It
   * is NOT the arrival: `loadMenu` mounts the surface `hidden`, so `uiHasFocus()` counts no
   * visible child on `stage` and the LOADING PLATE (on `cutscene`, z40, opaque) keeps the keyboard
   * exactly as it does on every other world. `menu.present()` is the arrival and it is called from
   * ONE place — the frame that releases that plate, far below. Presenting here instead would play
   * the whole 0.96 s entrance, arm a document-level focus trap and announce the greeting
   * underneath an opaque plate whose only control is the boot watchdog's `Try again`.
   *
   * Hard rule 9's EXISTING fence (`uiHasFocus()`, published by `ui/layers.ts`) is still the whole
   * freeze story; presenting is simply when it starts. Never a new fence.
   */
  let menu: MenuHandle | null = null;
  /** One-shot: the menu arrives on the first frame the loading plate leaves, and never again. */
  let menuPresented = false;
  if (menuRequired(world.requires)) {
    const { loadMenu } = await import('../ui/menu/loader');
    /*
     * ⭐ THE ACTION SEAM'S HOST END (main-menu plan §6, M3). The surface reports intent; THIS is
     * where intent becomes consequence. Three are built: `menu-resume`, (M4·C) `menu-streak` and
     * (M4·D) `menu-quest`.
     *
     * `menu-resume` — "Keep playing" — is the DISMISSAL of the vertical, so it is `dispose()`, and
     * dispose is already the one act that unmounts the surface, releases the focus trap and
     * therefore hands the keyboard back (hard rule 9: with no visible child on the input-owning
     * `stage` layer, `uiHasFocus()` goes false and the movement sources stop reading neutral).
     * There is no second un-freeze anywhere and there must never be one.
     *
     * The other FOUR ids are destinations plan §7 puts out of scope — the store, settings, the
     * calendar. They warn ONCE each and do nothing (rule 6: an unimplemented verb warns once and
     * never throws). Inventing a route to a screen that does not exist would be a product decision
     * taken in a wiring line.
     */
    const unbuilt = new Set<MenuActionId>();
    /*
     * ⭐ M4·D — THE THIRD CONSEQUENCE, AND THE ONLY ONE THAT LEAVES THE PAGE (plan §8.4).
     *
     * `menu-resume` is a LIFETIME (dispose) and `menu-streak` is a CHILD SURFACE (openStreak); this
     * one POSTs and then navigates, and both halves are the host's by construction — the menu
     * vertical holds no world id, names no endpoint and never touches `location`.
     *
     * The three lines below are the whole seam and each is deliberate:
     *   `questToStart()`   asks the SURFACE what it is offering. Mount never reads into the model:
     *                      the card and this answer come out of one pure fold, so the id can never
     *                      name a quest that is not on screen, and it is `null` while a launch is
     *                      already in flight — which is what makes a double press unable to fire a
     *                      second POST without a flag anywhere in this file.
     *   `setQuestLaunch()` hands the phase back so the card can draw it. There is no success arm:
     *                      the navigation is the success, and it takes this context with it.
     *   `await import()`   ⚠️ STARTING CONSUMES THE DAY'S QUEST ON LIVE, so the module that can do
     *                      it is not even fetched until a person has pressed the card. No boot
     *                      path, no retry and no test can reach it.
     */
    const launchDailyQuest = async (): Promise<void> => {
      const questId = menu?.questToStart() ?? null;
      // Nothing to start. The card already says so in words (all done, nothing offered, or the
      // board never arrived) and is inert, so this is unreachable through a real press — it is the
      // arm that makes that true rather than assumed.
      if (questId === null) return;
      menu?.setQuestLaunch('starting');
      const { startDailyQuest, dailyQuestUrl } = await import('./launchQuest');
      const result = await startDailyQuest(questId, location.search);
      // The menu can have been disposed while the POST was in flight (Resume is one press away).
      if (menu === null) return;
      if (!result.ok) {
        menu.setQuestLaunch('failed');
        return;
      }
      /*
       * ⚠️ A REAL NAVIGATION, NEVER AN IN-PLACE SWAP. The exploration vertical is gated ONCE per
       * page from the boot world's `requires`, so swapping the world in place could never add it —
       * and an in-place swap has already dropped `&axon=mock` once and put a session on the wrong
       * backend. `dailyQuestUrl` rewrites only the `world` param and preserves every other one.
       */
      location.assign(dailyQuestUrl(location.search, location.pathname, location.hash));
    };
    menu = await loadMenu({
      search: location.search,
      onAction: (id) => {
        if (id === 'menu-resume') {
          menu?.dispose();
          menu = null;
          return;
        }
        /*
         * ⭐ M4·C — THE SECOND CONSEQUENCE, AND THE FIRST ONE THAT IS NOT A LIFETIME. The flame in
         * the top bar opens the streak panel: a modal drawn from the model the menu already holds,
         * mounted inside the menu's own root, on the same input-owning layer. So this arm is a
         * NAVIGATION and not a dismissal — the vertical stays up, `uiHasFocus()` stays true, and
         * there is nothing here to un-freeze.
         *
         * There is deliberately no `menu-streak-close` arm to write: the panel closes itself on
         * Escape, on the scrim and on its ✕. A way OUT of a focus-trapped surface that depended on
         * this line would be a keyboard trap the day somebody edited it.
         */
        if (id === 'menu-streak') {
          menu?.openStreak();
          return;
        }
        // ⭐ M4·D — the day's quest. `launchDailyQuest` is async and this handler is not, so the
        // promise is explicitly floated: every failure inside it is already a STATE the card draws,
        // and there is nothing here that could act on a rejection.
        if (id === 'menu-quest') {
          void launchDailyQuest();
          return;
        }
        if (unbuilt.has(id)) return;
        unbuilt.add(id);
        console.warn(`[menu] '${id}' has no destination in this build yet.`);
      },
    });
  }
  /*
   * Rung 4 → 5. `spawnEntities()` owns both ends of the gate now (L3.C): it lowers the latch,
   * restarts the deadline, stamps `placing`/`streaming`, binds every slot and asks once at the end
   * whether anything was required at all. Neither end blocks boot — the loop starts either way and
   * the SIM is what waits.
   */
  await spawnEntities();

  /*
   * ── ⭐ THE WORLD'S OWN BAKED LIGHTING, LOADED BECAUSE ITS DATA SAYS SO (BUG 1) ─────────────────
   *
   * NOT dev-gated, and that is the entire fix. Until this line the only thing that could turn GI on
   * was a `?gi=` parameter inside `if (import.meta.env.DEV)` in `scene.ts`, so opening
   * `?world=sponza-debug-01` normally gave no GI over a 912 KB atlas sitting on disk beside a
   * world.json that asks for it — and a production build had no path to an atlas at all.
   * `world.lighting.indirect` now drives it; the URL survives as a DEV override that still wins.
   *
   * ⚠️ `onAllLoaded`, NOT `onAllSpawned`, and this is the single most likely way to ship this
   * feature broken — it would pass on the gym and fail on Sponza. `staticMesh.create()` returns
   * synchronously and adds its GLB inside a `.then()`, so `onAllSpawned` fires while 50 MB of glTF
   * is still parsing; `drawnBounds()` is a near-empty box there, and `checkProvenance` refuses the
   * atlas for `geometryDrift` on EVERY boot with a plausible, self-consistent, completely wrong
   * verdict reported to the artist as `stale`.
   *
   * FIRE AND FORGET (D25): the fetch may be slow, may fail, or may never arrive, and the world
   * renders either way — the constant fill carries the indirect term until the volume binds.
   */
  host.onAllLoaded(() => {
    void scene.applyBakedLighting();
    /*
     * ── ⭐ THE WARM-UP THAT ACTUALLY HAS SOMETHING TO WARM ────────────────────────────────────────
     *
     * The `await scene.warmup()` below runs before any of this world's meshes exist, so it compiles
     * an EMPTY scene — see that method's header for the measurement. This is the same call at the
     * one moment it can do its job: every entity has loaded, so every geometry and material the
     * level will draw is now real.
     *
     * ⚠️ `flush()` FIRST, AND NOT AS TIDINESS. The batcher coalesces its rebuild to one tick after
     * the last `add`, so at this instant the placements have all arrived and the `InstancedMesh`es
     * they become may not exist yet. Warming before the flush would compile the pipelines of a
     * scene the next tick throws away, which is the same mistake as warming an empty one — just
     * later and harder to see.
     *
     * Fire and forget, like `applyBakedLighting` beside it and for the same reason: the world must
     * render whether or not this finishes, and the L3 gate is what decides when it is playable.
     */
    /*
     * ⭐ `seal()` BEFORE `flush()`, AND BOTH BEFORE `warmup()`. Sealing prices each group's cell
     * now that every placement has arrived (see `StaticBatcher.seal`); flushing builds what that
     * decided; warming compiles what flushing built. Any other order compiles a scene the next
     * step throws away, which is the mistake the comment above already describes once.
     */
    staticBatcher.seal();
    staticBatcher.flush();
    void scene.warmup();
  });

  // Shader warm-up (rendering plan, R1): precompile the fully built scene before the loop so
  // first frames don't stall on pipeline compilation. The async-loaded character compiles later
  // on arrival — acceptable until it moves into the loading gate.
  await scene.warmup();

  const detachKeys = keyboard.attach(window);
  const detachMouse = mouse.attach(scene.renderer.domElement);

  // Audio unlocks on the first user gesture (browser autoplay policy).
  const sfx = new Sfx();
  const unlockAudio = () => sfx.unlock();
  window.addEventListener('pointerdown', unlockAudio);
  window.addEventListener('keydown', unlockAudio);

  const debugOpts: DebugOpts = {
    showArc: true,
    sound: true,
    rumble: true,
    probeGrid: false,
  };

  // Spawn resolution (ADR-0002): player_start entities first, world.spawn as the back-compat
  // fallback. Recomputed per respawn — editor edits can move/add starts mid-session. Facing yaw
  // is resolved but not yet wired into the camera/character (presentation nicety, noted in ADR).
  const spawnPos = () => v3(...resolveSpawn(world).pos);
  let prev = createMotionState(spawnPos(), tuning);
  let curr = prev;
  // The INTERPOLATED render pose, written once per frame and read by anything that must agree
  // with what is on screen rather than with the raw sim position (they differ by up to 11.6 cm).
  const renderFeet: { x: number; y: number; z: number } = { ...curr.pos };

  // ── PHASE-0 AXON SPIKE (throwaway). Declared HERE, after `curr`/`renderFeet`, and not beside
  // the other loaders: `installAxonSpike` reads the player pose SYNCHRONOUSLY to place the hull,
  // so an earlier call site would be a temporal-dead-zone crash — the exact bug that killed the
  // first X1 playtest (see the `tickCount` note above). Folded out of any production build by
  // AXON_SPIKE; opt-in per session with `?axon-spike=1`. ──
  let axonSpike: AxonSpikeHandle | null = null;
  // `=1` runs the redesigned proxemics model; `=legacy` runs the one that FAILED Gate A, so the
  // before/after table is two runs of ONE build rather than a claim about a build nobody can
  // re-run. Anything else installs nothing.
  const spikeParam = new URLSearchParams(location.search).get('axon-spike');
  if (AXON_SPIKE && (spikeParam === '1' || spikeParam === 'legacy')) {
    const { installAxonSpike } = await import('../debug/axonSpike');
    axonSpike = installAxonSpike({
      scene,
      world,
      model: spikeParam === 'legacy' ? 'coverage-utility' : 'occlusion-cost',
      player: () => ({
        pos: curr.pos,
        vel: curr.vel,
        yawDeg: (scene.rig.yaw * 180) / Math.PI,
        speed: speedXZ(curr),
        grounded: curr.grounded,
        mode: curr.mode,
      }),
      playerRenderPos: () => renderFeet,
    });
  }

  // ── Editor mode (A3 + ADR-0002 gating): DEV-ONLY — in a prod build (no VITE_ENABLE_EDITOR=1)
  // none of the paths below exist: no F2 handler, no panel button, no reachable lazy import. ──
  let editorActive = false;
  let editor: Editor | null = null;
  let toggleEditor: (() => void) | undefined;
  /**
   * The gameplay-dev tuning pane (S4): null outside DEV, and null in DEV until its lazy chunk lands
   * below. Declared HERE because `enterEditor`/`exitEditor` and the editor's `tuning` seam close
   * over it; they run long after mount returns, so the `let` is never read in its dead zone.
   */
  let pane: Pane | null = null;
  /** The editor's dev-only corner badge (S4) — a mouse's F2. Hidden while the editor is open. */
  let editorBadge: EditorBadgeHandle | null = null;
  /** F3: raise/lower the tuning pane. Undefined outside DEV (no pane to raise). */
  let toggleTuning: (() => void) | undefined;
  /**
   * THE RUNTIME PERF BAR (`src/debug/perfBar.ts`) — the owner's "an FPS and other stats bar at the
   * top, including some perf controls ... not for the editor but the runtime". Dev-only, lazy, and
   * `null` in every production build. `update(dt)` is called from the animation callback below;
   * everything else it needs it POLLS, so mount holds no perf state of its own.
   */
  let perfBar: PerfBarHandle | null = null;
  let togglePerfBar: (() => void) | undefined;
  /**
   * The dev hook's door (EDITOR v2 · S1): load the lazy chunk if needed and enter editor mode.
   * `__fwg.editor` is a live getter that is null until the chunk has landed — an agent calls
   * `openEditor()` first, the way a human presses F2 first. Undefined when the gate is closed.
   */
  let openEditorForHook: (() => Promise<boolean>) | undefined;
  const onF2 = (e: KeyboardEvent) => {
    if (e.code !== 'F2' || !toggleEditor) return;
    if (uiHasFocus()) return; // input ownership: the UI holds the keyboard — hands off, no preventDefault
    e.preventDefault();
    toggleEditor();
  };
  /**
   * F3 summons the gameplay-dev tuning pane (S4). Same shape as F2, same fence (hard rule 9): the
   * ownership check comes BEFORE the preventDefault, so a card that holds the keyboard sees an
   * untouched F3. Refused while the editor is open — the editor is the modal then, and the pane it
   * would raise is hidden under the right dock anyway.
   */
  const onTuning = (e: KeyboardEvent) => {
    if (e.code !== 'F3' || !toggleTuning) return;
    if (uiHasFocus() || editorActive) return; // input ownership first; the editor owns the keyboard while open
    e.preventDefault();
    toggleTuning();
  };
  /**
   * F4 — the runtime perf bar. Gated EXACTLY like `onTuning` above and for the same reason
   * (hard rule 9): `uiHasFocus()` is consulted BEFORE `preventDefault()`, so a refused key passes
   * through untouched and a card's own typing still works. Unlike F3 it is NOT refused while the
   * editor is open — the editor has its own FPS chip, and the bar's whole value there is that it
   * SAYS the editor is freezing dynamic resolution. It is simply never installed in production.
   */
  const onPerfBar = (e: KeyboardEvent) => {
    if (e.code !== 'F4' || !togglePerfBar) return;
    if (uiHasFocus()) return; // input ownership first
    e.preventDefault();
    togglePerfBar();
  };
  if (editorEnabled(BUILD_ENV)) {
    const enterEditor = () => {
      if (editorActive || !editor) return;
      editorActive = true;
      // Batching OFF: the editor selects by `object3d → placement index`, which a shared
      // `InstancedMesh` does not provide (see `EntityCtx.batchStatic`). Clearing here and letting
      // the editor's own respawn draw a mesh per placement is what keeps selection working.
      batchingEnabled = false;
      staticBatcher.clear();
      document.exitPointerLock();
      // Dynamic resolution is FROZEN in the editor: the sim is stopped, the frame cost is
      // nothing like gameplay's, and a rung change while someone is placing a box is noise.
      scene.resolution.freeze('editor');
      scene.setArc(null, []); // hide the jump arc while frozen
      host.setAllEditorVisible(true); // editor-only markers (player starts) appear
      // The badge is a door INTO the editor; while the editor is open its corner is the status
      // strip's. (The tuning pane's hide/restore is the editor's own `EditorCtx.tuning` seam.)
      editorBadge?.setVisible(false);
      editor.open();
    };
    const exitEditor = () => {
      if (!editorActive || !editor) return;
      editorActive = false;
      // Play re-batches: `exitEditor` already respawns every entity below, so each placement offers
      // itself to the batch again on its way back in.
      batchingEnabled = true;
      editor.close(); // restores the tuning pane to its pre-open state through `EditorCtx.tuning`
      editorBadge?.setVisible(true);
      scene.resolution.unfreeze('editor');
      host.setAllEditorVisible(false); // editor-only markers hide again
      // Physics + entities REBUILT from the edited world; player teleports to the resolved spawn.
      // `resetLevel` drops the entity bodies with the level; `spawnEntities` rebinds them (B3).
      physics.resetLevel(world.boxes, world.platforms);
      host.dispose();
      void spawnEntities();
      curr = createMotionState(spawnPos(), tuning);
      prev = curr;
      accumulator = 0;
      keyboard.consumeTick(scene.rig.yaw); // drop edge latches from editor-mode typing
    };
    const ensureEditor = async (): Promise<void> => {
      if (editor) return;
      // ADR-0001 "download partial": creator tooling loads on first use, never at boot.
      const { createEditor } = await import('../editor/editor');
      editor = createEditor({
        scene,
        host,
        world,
        canvas: scene.renderer.domElement,
        sessionAssets,
        requestPlay: () => exitEditor(),
        requestOpen: () => enterEditor(),
        // S4: the ONE definition of "the tuning pane is up" is Tweakpane's own `hidden`; the editor
        // hides it on open and hands it back on close. Absent when no pane exists.
        tuning: {
          setVisible: (visible) => {
            if (pane) pane.hidden = !visible;
          },
          visible: () => pane !== null && !pane.hidden,
        },
        // S5: the editor's window onto the collision binder — the SAME `bindCollision` the boot
        // path uses, so a mesh the editor spawns is solid in Play; `pose` is the 0.2 ms re-pose an
        // editor move takes (false ⇒ nothing bound, or the scale changed and a `bind` is owed).
        collision: {
          bind: (index, inst) => bindCollision(index, inst),
          clear: () => {
            physics.clearEntityCollision();
            boundGen.clear();
            scene.syncDebugView();
          },
          pose: (index) => {
            const ok = physics.poseEntityCollision(index, placementPose(world.entities[index]));
            if (ok) scene.syncDebugView();
            return ok;
          },
          info: () => physics.entityCollisionInfo(),
        },
      });
    };
    toggleEditor = () => {
      void (async () => {
        await ensureEditor();
        if (editorActive) exitEditor();
        else enterEditor();
      })();
    };
    openEditorForHook = async () => {
      await ensureEditor();
      enterEditor();
      return editorActive;
    };
    window.addEventListener('keydown', onF2);
  }

  const readouts = {
    backend: scene.backend,
    tier: scene.tier as number,
    vramMB: 0,
    mode: 'ground',
    speed: 0,
    vy: 0,
    grounded: true,
    hoverFuelMs: tuning.hoverDurationMs,
    coyoteMs: 0,
    bufferMs: 0,
    gamepad: '—',
    surface: 'default',
  };
  /**
   * How often the frame loop refreshes `readouts.vramMB`. It is a whole-scene census, its only
   * consumer is the dev tuning pane, and 4 Hz is already faster than anyone reads a number — see the
   * note at the write site for what it cost per frame before it was throttled.
   */
  const VRAM_READOUT_INTERVAL_MS = 250;
  let lastVramAtMs = 0;

  // ── The gameplay-dev tuning pane + the editor badge (S4): lazy, DEV-folded, summoned. See
  // DEV_TUNING above. `readouts`/`debugOpts` stay here because the loop writes them regardless. ──
  if (DEV_TUNING) {
    const { createPanel } = await import('../debug/panel');
    const tuningParam = new URLSearchParams(location.search).get('tuning');
    pane = createPanel(
      tuning,
      derived,
      readouts,
      tuningFile,
      refreshDerived,
      (name) => {
        Object.assign(tuning, getPreset(tuningFile, name));
        refreshDerived();
        pane?.refresh();
      },
      () => {
        const hash = `#p=${btoa(JSON.stringify(tuning))}`;
        history.replaceState(null, '', hash);
        void navigator.clipboard?.writeText(location.href).catch(() => {});
      },
      debugOpts,
      // onProbeGrid only when the editor gate is open (dev-gated like the editor): the vendored GI
      // probe grid is a dev evaluation toggle, lazy-chunked behind scene.setProbeGrid.
      {
        rig: scene.rig,
        camTune: scene.camTune,
        charFix: scene.charFix,
        onProbeGrid: editorEnabled(BUILD_ENV)
          ? (on: boolean) => void scene.setProbeGrid(on)
          : undefined,
        // `?tuning=1` shows it at boot; otherwise F3. The pane never reads the URL itself.
        visible: tuningParam === '1',
      },
    );
    const thePane = pane;
    toggleTuning = () => {
      thePane.hidden = !thePane.hidden;
    };
    window.addEventListener('keydown', onTuning);
    /*
     * ⭐ THE PERF BAR POLLS; IT IS HANDED NO STATE. Every number comes from `scene.resolution.state()`
     * and `scene.renderStats()` at paint time, and the four `apply` handles are the SAME ones the
     * editor's Settings tab drives (`renderSettings.SettingsApplyDeps`) — not a second set. A
     * `reload` row navigates, which is honest in the runtime: there is no unsaved world to lose.
     */
    const { installPerfBar } = await import('../debug/perfBar');
    const { describeGpu, GPU_REMEDY } = await import('../render/gpuAdapter');
    const { bindRenderSettings } = await import('../editor/renderSettings');
    const bootDpr = Number(new URLSearchParams(location.search).get('dpr'));
    const bound = bindRenderSettings(
      {
        resolution: scene.resolution,
        csm: () => scene.csm(),
        setCsmOverride: (n) => scene.setCsmOverride(n),
        shadowRes: () => scene.shadowRes(),
        setShadowRes: (n) => scene.setShadowResScale(n),
        get tier() {
          return scene.tier;
        },
        get backend() {
          return scene.backend;
        },
        samples: () => (scene.renderer as { samples?: number }).samples ?? 0,
        /*
         * ⚠️ `1009` IS `THREE.UnsignedByteType`, SPELLED AS A NUMBER ON PURPOSE. `mount.ts` does not
         * import `three` and must not start — it is the eager composition root, and the editor's
         * copy of this thunk is where the named constant lives. three's texture-type constants are
         * frozen public API (`constants.js`), so the literal is stable; the alternative is dragging
         * a namespace import into the entry chunk to read one integer.
         */
        outbuf: () =>
          (scene.renderer as { getOutputBufferType?: () => number }).getOutputBufferType?.() === 1009
            ? 'u8'
            : 'f16',
      },
      Number.isFinite(bootDpr) && bootDpr > 0 ? bootDpr : null,
    );
    perfBar = installPerfBar({
      poll: () => {
        const r = scene.resolution.state();
        const info = scene.renderer.info.render;
        return {
          displayPeriodMs: r.displayPeriodMs,
          gpuMs: r.gpuTimeMs,
          gpuTimingAvailable: r.gpuTimingAvailable,
          scale: r.scale,
          pinned: r.pinned !== null,
          bufferW: r.drawingBufferWidth,
          bufferH: r.drawingBufferHeight,
          backend: scene.backend,
          tier: scene.tier,
          frozen: r.frozen,
          starved: r.starved,
          // ⚠️ THE LATCH, NOT `renderer.info.render`. This poll runs on its own timer, outside the
          // frame, and three resets `info` once per ANIMATION FRAME — so reading it here landed
          // between the reset and the draw and reported `0 draws · 0 tris` over a scene drawing
          // 4,270 of them. See `GameScene.frameStats`.
          ...scene.frameStats(),
          vramMB: scene.getVramMB(),
          settings: bound.read(),
          gpu: describeGpu(scene.gpu),
          gpuHelp: GPU_REMEDY,
          // P1: the library row (resident / in-flight / MB / evictions). Polled like every other
          // number here — the bar computes nothing and holds nothing.
          library: assets.stats(),
        };
      },
      apply: bound.apply,
      reloadWithParam: (param, value) => {
        const url = new URL(location.href);
        if (value === null) url.searchParams.delete(param);
        else url.searchParams.set(param, value);
        location.assign(url.toString());
      },
      visible: new URLSearchParams(location.search).get('perf') === '1',
    });
    const theBar = perfBar;
    togglePerfBar = () => theBar.toggle();
    window.addEventListener('keydown', onPerfBar);
    // A mouse's door into the editor — only where the editor gate is open (it is what F2 does).
    if (editorEnabled(BUILD_ENV)) {
      const { installEditorBadge } = await import('../debug/editorBadge');
      editorBadge = installEditorBadge({ open: () => toggleEditor?.() });
    }
  }

  // ── DEV backend controls (X2-S5.5): badge + backend/fixture pickers + debug buttons. Folded
  // out of production entirely (see DEV_BACKEND above); attaches to the pane just built. ──
  let disposeDevBackend: (() => void) | null = null;
  if (DEV_BACKEND) {
    const { installDevBackend } = await import('../debug/devBackend');
    disposeDevBackend = installDevBackend({
      pane,
      search: location.search,
      // The badge names the ACTIVE backend, so it only appears where a session actually boots.
      hasSession: explorationRequired(world.requires),
      // Read fresh on click — the handle is the ONE door "skip to end" reaches the orchestrator
      // through, and the pane must never capture a stale one across an editor rebuild.
      exploration: () => exploration,
      // Dynamic resolution: readouts + the rung pin (docs/ai/plans/dynamic-resolution.md).
      resolution: scene.resolution,
    }).dispose;
  }

  /*
   * ── THE GI BAKE-STATE BADGE. It polls `scene.gi()` itself (500 ms) rather than being fed from
   * the frame loop: the state settles asynchronously in the load bracket long after mount, and a
   * per-frame read would put a dev-only object build inside the steady-state frame the GI design
   * is measured in. `null` from `gi()` hides it — see the module header for why that is the
   * honest answer rather than "UNBAKED". ──
   */
  let disposeGiBadge: (() => void) | null = null;
  if (GI_BADGE) {
    const { installGiBadge } = await import('../debug/giBadge');
    disposeGiBadge = installGiBadge(() => {
      const gi = scene.gi();
      return gi === null
        ? null
        : {
            bakeState: gi.bakeState,
            source: gi.source,
            quality: gi.quality,
            probes: gi.probes,
            stale: gi.stale,
          };
    }).dispose;
  }

  // Alt+P = A/B swap without position reset — the only honest way to judge a 15 ms tuning change.
  // Slot B starts as ue-parity so the "what we had vs what we're building" demo is one keypress.
  // It was `Tab` until the input-ownership fix; see src/app/presetSwap.ts for why that was a bug.
  const presetSwap = createPresetSwap({
    tuning,
    slotB: getPreset(tuningFile, 'ue-parity'),
    uiHasFocus,
    onSwapped: (slot) => {
      refreshDerived();
      pane?.refresh();
      // It changes how the game FEELS with no visual tell — say so, every time.
      console.info(
        `[tuning] ${PRESET_SWAP_LABEL}: slot ${slot} · maxRunSpeed ${tuning.maxRunSpeed}`,
      );
    },
  });
  const onPresetSwap = (e: KeyboardEvent) => {
    if (presetSwap.handle(e)) e.preventDefault(); // preventDefault ONLY on an accepted swap
  };
  window.addEventListener('keydown', onPresetSwap);

  let accumulator = 0;
  let last = performance.now();
  /** L3.D · ms from navigation to the first TURNED frame. 0 until the loop has run once. */
  let firstFrameAtMs = 0;
  scene.renderer.setAnimationLoop(() => {
    /*
     * ⭐ O2 · A BAKE OWNS THE FRAME — NOTHING ELSE RUNS WHILE ONE IS RUNNING.
     *
     * Before O2 a bounce pass was one synchronous JS turn and this callback simply could not fire
     * during it. Stage A drains the submission queue at every chunk boundary (the bake is
     * queue-depth-bound: drain scales as passes², so the window has to be a chunk rather than a
     * pass), which opens ~361 yields on a Sponza bake — and a sim tick in any one of them poses
     * the character, moves the platforms and steps every entity IN THE SCENE THE BAKE IS
     * PHOTOGRAPHING. Half an atlas of one arrangement and half of another, with every downstream
     * check green, is precisely the failure the "do not Save during a bake" rule exists to stop;
     * this is the same door, opened from inside.
     *
     * `last` is deliberately NOT advanced here, and the clamp above is why that is safe: the first
     * frame after a six-minute bake sees a `frameDt` of 0.25 s, not 360, so the accumulator cannot
     * spiral. The bake's own progress reaches the console rather than the frame — measured, the
     * console channel is live through a frozen main thread and a round trip is not.
     */
    if (scene.bakeHoldsFrame()) return;
    /*
     * ⭐ WINDOW A, FINALLY MEASURED. Navigation → the first frame this loop actually turns. The
     * canvas does not exist for most of that window and draws nothing for the rest, and until this
     * line nobody had a number for it — the only boot figure anyone quoted was the gate's, which
     * is the END of window B. Stamped here rather than after `scene.render()` because every branch
     * below renders, and a first-frame number that skipped the not-yet-playable frames would be
     * measuring the wrong window entirely.
     */
    if (firstFrameAtMs === 0) firstFrameAtMs = nowMs();
    /*
     * ⭐ P1 · `assets.tick()` IS THE LIBRARY'S ONE DISPOSAL TRIGGER, AND IT LIVES HERE.
     *
     * The ledger's grace period is pure arithmetic over an injected clock: `expire()` NAMES the
     * sources whose refcount hit zero more than `graceMs` ago and REMOVES NOTHING from the GPU —
     * the service's disposal walk does that, and it runs from here. `acquire`/`release` tick
     * opportunistically so a caller that never ticks still converges, but a source whose last
     * handle went away while nothing else happened would otherwise stay resident until the next
     * acquire. One call per frame, ahead of every early return below (the editor branch, the
     * not-yet-playable branch), because a frozen sim is exactly when a swap's old body expires.
     */
    assets.tick();
    /*
     * …and the library's half of the gate, polled for the ONE transition it can make. Nothing in
     * the service calls back when a handle settles, and `refreshResidency` is otherwise driven
     * only by collider binds — so on a world whose colliders bound before the character's clips
     * arrived, the gate would wait for the 20 s deadline. This is a boolean edge, not a poll:
     * `bootGate()` is a fold over a handful of handles, and it stops being read the moment the
     * gate opens.
     */
    if (!worldResident && !libraryGateSeen && assets.bootGate().allResident) {
      libraryGateSeen = true;
      refreshResidency();
    }
    const now = performance.now();
    const frameDt = Math.min((now - last) / 1000, 0.25);
    last = now;
    // The bar's ONE input. It is fed the RAW frame interval in ms — the cadence a player feels —
    // and not `resolution.state().frameMs`, which stops filling the moment the editor freezes the
    // controller (the exact trap `editorRenderSettings` pins the editor's own chip against).
    perfBar?.update(frameDt * 1000);

    // Editor mode: the sim is FROZEN — no ticks, no input consumption, no camera rig. Rendering
    // continues, platforms stay posed at the frozen sim time, the editor drives the camera.
    if (editorActive) {
      editor?.update(frameDt);
      scene.updatePlatforms(Math.max(0, tickCount - 1) * FIXED_DT);
      scene.render();
      return;
    }
    /*
     * ⭐ THE WORLD IS NOT PLAYABLE YET (S7) — RENDER, BUT DO NOT SIMULATE.
     *
     * Same shape as the editor branch above and as `bakeHoldsFrame()`: frames keep going so the
     * canvas is not black and the warm-up has something to do, platforms hold their t=0 pose, and
     * the fixed-tick accumulator does NOT advance — so the player integrates no gravity against a
     * physics world whose floor has not bound. `accumulator` is untouched rather than reset,
     * because `frameDt` is already clamped to 0.25 s and there is nothing banked to spiral on.
     */
    if (!worldResident) {
      scene.updatePlatforms(0);
      scene.render();
      return;
    }
    accumulator += frameDt;

    // Gamepad is polled, not evented — one snapshot per frame feeds the per-tick edge latches.
    gamepad.poll();

    // Camera input at render rate (mouse + right stick turn the camera; character orients to
    // movement).
    const { dx, dy } = mouse.consumeDeltas();
    const look = gamepad.consumeLook(frameDt);
    if (dx !== 0 || dy !== 0 || look.active) scene.rig.msSinceCamInput = 0;
    scene.rig.yaw += look.dYaw - dx * MOUSE_SENS;
    scene.rig.pitch = clamp(scene.rig.pitch + dy * MOUSE_SENS + look.dPitch, -0.15, 1.1);

    let ticks = 0;
    const frameEvents: FeedbackEvent[] = [];
    while (accumulator >= FIXED_DT && ticks < MAX_TICKS_PER_FRAME) {
      // Edge latches clear on first consumption, so catch-up ticks naturally see held-only input.
      const input = mergeTickInputs(
        keyboard.consumeTick(scene.rig.yaw),
        gamepad.consumeTick(scene.rig.yaw),
      );
      physics.advancePlatforms(tickCount * FIXED_DT);
      tickCount++;
      prev = curr;
      curr = stepMotion(curr, input, tuning, derived, FIXED_DT, physics, surfaces);
      frameEvents.push(...deriveFeedback(prev, curr, tuning));
      accumulator -= FIXED_DT;
      ticks++;
    }
    if (ticks === MAX_TICKS_PER_FRAME) accumulator = 0;

    // Entities update once per FRAME (they are presentation-side reactions, not sim), and their
    // events join this frame's bus before any subscriber below sees it.
    const snapshot: PlayerSnapshot = {
      pos: curr.pos,
      speed: speedXZ(curr),
      rolling: curr.rollMsLeft > 0,
    };
    host.update(snapshot, frameDt);
    if (pendingEntityEvents.length > 0) {
      frameEvents.push(...pendingEntityEvents);
      pendingEntityEvents.length = 0;
    }

    // Exploration frame drive (X1d): after entities updated (overlaps/respawn requests fresh),
    // on the sim clock. Null when no exploration world is loaded; paused with the editor.
    explorationTick?.(tickCount * FIXED_DT * 1000);

    sfx.enabled = debugOpts.sound;
    sfx.handle(frameEvents);
    sfx.update(curr, frameDt);
    gamepad.enabled = debugOpts.rumble;
    gamepad.rumble(frameEvents);

    // Fell off the world? Respawn at the resolved spawn point (player_start aware, ADR-0002).
    if (curr.pos.y < -20) {
      curr = createMotionState(spawnPos(), tuning);
      prev = curr;
    }

    const alpha = accumulator / FIXED_DT;
    // Platforms render at the sim time the INTERPOLATED player matches: tick k ran on platform
    // poses at k·dt (advancePlatforms is called with the pre-increment count), so curr is glued
    // to (tickCount−1)·dt and prev to (tickCount−2)·dt — lerping prev→curr by alpha lands on
    // (tickCount−2+alpha)·dt. Anything later makes a ridden platform visibly lead the character.
    renderTimeS = (tickCount - 2 + alpha) * FIXED_DT;
    scene.updatePlatforms(renderTimeS);
    // `syncPlayer` keeps its own freshly allocated argument, exactly as before: `renderFeet` is a
    // long-lived object and handing a reused one across a seam that might retain it is a class of
    // bug nobody would find. It is a COPY for readers outside the loop, nothing more.
    const feet = {
      x: prev.pos.x + (curr.pos.x - prev.pos.x) * alpha,
      y: prev.pos.y + (curr.pos.y - prev.pos.y) * alpha,
      z: prev.pos.z + (curr.pos.z - prev.pos.z) * alpha,
    };
    renderFeet.x = feet.x;
    renderFeet.y = feet.y;
    renderFeet.z = feet.z;
    scene.syncPlayer(feet, curr, frameEvents);
    // The spike ticks AFTER syncPlayer so it reads THIS frame's camera pose — Axon reads the
    // camera's current pose and is NEVER an input to `physics.cameraProbe` (plan §3.2).
    // `renderTimeS` (not the sim clock) is what the renderer posed the platforms at: two ticks of
    // disagreement is ~5 cm of platform travel, enough to hover visibly ahead of the ride.
    axonSpike?.update(frameDt, renderTimeS);

    if (debugOpts.showArc && curr.grounded) {
      scene.setArc(
        curr.pos,
        predictJumpArc({ x: curr.vel.x, y: 0, z: curr.vel.z }, tuning, derived),
      );
    } else {
      scene.setArc(null, []);
    }

    readouts.mode = curr.teetering ? 'teeter' : curr.mode;
    readouts.speed = speedXZ(curr);
    readouts.vy = curr.vel.y;
    readouts.grounded = curr.grounded;
    readouts.hoverFuelMs = curr.hoverFuelMs;
    readouts.coyoteMs = curr.coyoteMsLeft;
    readouts.bufferMs = curr.jumpBufferMsLeft;
    readouts.gamepad = gamepad.connected() ? 'connected' : '—';
    readouts.surface = curr.groundSurface;
    /*
     * ⚠️ THIS ONE IS NOT A FIELD READ LIKE THE EIGHT ABOVE IT — IT IS A WHOLE-SCENE CENSUS.
     *
     * `getVramMB()` walks every object, geometry and texture in the scene and sums their bytes. It
     * sat in this list looking like its neighbours and ran on EVERY FRAME, for a readout whose only
     * consumer is the DEV tuning pane — which is lazy, is absent unless F3 or `?tuning=1` opened it,
     * and which POLLS this same number on its own timer anyway (`read()` above returns
     * `vramMB: scene.getVramMB()`). On a level with 5,563 placements the census is a multi-
     * millisecond walk, so a shipped build was paying it sixty times a second to fill a field
     * nothing would ever read.
     *
     * Gated on the one consumer and throttled to the rate a human can read. The pane's own poll is
     * unchanged and is still the authority — this is a convenience mirror, and now says so.
     */
    if (pane !== null && performance.now() - lastVramAtMs >= VRAM_READOUT_INTERVAL_MS) {
      lastVramAtMs = performance.now();
      readouts.vramMB = scene.getVramMB();
    }

    scene.render();

    /*
     * ── ⭐ L4 · THE PLATE LEAVES HERE, AND NOWHERE ELSE ──────────────────────────────────────────
     *
     * Reaching this line means the gate is open (the `!worldResident` branch above returns) and
     * this frame has been driven by the SIM — the accumulator advanced, the platforms moved, the
     * character was posed and the frame is on the glass. That is the one-frame rule: fading on the
     * instant `worldResident` flips would show the player a frozen world fading in and then
     * jerking into motion, which reads worse than the freeze it replaced.
     *
     * `release()` reparents to the non-input-owning `hud` layer FIRST and fades second, so input
     * comes back to the game on this frame rather than 620 ms later; the flag only spares the
     * closure a function call on every subsequent frame of the session.
     */
    if (screen && !screenLeaving) {
      screenLeaving = true;
      void screen.release();
    }

    /*
     * ── ⭐ …AND THE MENU ARRIVES ON THE SAME FRAME, FOR THE SAME REASON ──────────────────────────
     *
     * The menu vertical was BUILT during the load phase (see `menuRequired` above) and mounted
     * `hidden`; this is the first moment its surface is genuinely on screen, so this is where its
     * entrance, its focus trap and its greeting begin. Reached whether or not a plate exists — the
     * loading screen is best-effort (rule 6) and a boot without one must still show the menu.
     *
     * `present()` is idempotent, so the one-shot latch buys the closure a call per frame and
     * nothing else; the correctness is in `present()` itself.
     */
    if (!menuPresented) {
      menuPresented = true;
      menu?.present();
    }
  });

  // Installed LAST: everything it reads (curr/tuning/exploration/scene) is live by now, and the
  // render loop is already turning so `ready()` flips on its own. DEV-only — see DEV_HOOK above.
  let disposeDevHook: (() => void) | null = null;
  if (DEV_HOOK) {
    const { installDevHook } = await import('../debug/devHook');
    disposeDevHook = installDevHook({
      worldId: world.worldId,
      scene,
      host,
      tuning,
      /*
       * ⭐ C · §9's P3 GATE, REACHABLE: `__fwg.summon('dev-sim-child')` in a world that never
       * referenced the asset. A PASS-THROUGH onto the ONE spawn path (`librarySpawn.summon`), for
       * the reason the editor hook's seventh law gives — a door with its own implementation is a
       * second system, and every test written through it stops being evidence about the one a
       * person uses. DEV-only by construction: `installDevHook` is itself inside mount's DEV fold.
       */
      summon: (id, overrides) => {
        const lib = libraryEntities;
        if (lib === null) return null;
        const summoned = lib.summon(id, overrides === undefined ? {} : { overrides });
        return {
          id: summoned.id,
          spawned: summoned.ready.then((inst) => inst !== null),
          release: () => summoned.release(),
        };
      },
      player: () => ({
        x: curr.pos.x,
        y: curr.pos.y,
        z: curr.pos.z,
        speed: speedXZ(curr),
        grounded: curr.grounded,
        mode: curr.mode,
      }),
      exploration: () => exploration,
      // The Phase-0 SPIKE wins when it is installed (`?axon-spike=1`) — it is the Gate A/B
      // measurement instrument and its ledger must not be shadowed by the shipped companion's.
      // Otherwise the real `axon_companion` agent's ledger, or null on a world with no companion.
      companion: () => axonSpike?.debug() ?? exploration?.companion?.() ?? null,
      // The editor's agent door (S1): the hook the editor built, or null before the chunk lands.
      // Read fresh on every access — the editor is created lazily on the first F2/openEditor.
      editor: () => editor?.hook() ?? null,
      // S5: what the physics world holds for every static-mesh placement, from the binder's side.
      collision: () => physics.entityCollisionInfo(),
      /*
       * L3: the boot sequence as state. Composed HERE, from the one `LoadTracker` and the one
       * `residency()` read, because those are the two facts and neither belongs to the hook. Read
       * fresh on every access — a captured snapshot would report the phase the hook was installed
       * at, which is `streaming` on every boot and therefore always plausible and always wrong.
       */
      // P1: `window.__fwg.library` — plan §8's "state, not pixels". "The character is resident
      // via the catalog" and "a body swap returned resident and MB to baseline" are claims only
      // this row can make, and no screenshot can.
      library: () => assets.stats(),
      load: () => {
        const r = readResidency();
        const lib = readLibraryResidency();
        return {
          phase: load.phase,
          history: load.history,
          placed: host.placedCount(),
          entities: world.entities.length,
          required: r.required.length,
          bound: r.bound,
          broken: r.broken.length,
          // P1 · the library's ONE counter. Distinct boot-lane ids, and how many have SETTLED
          // (ready or failed). Both 0 on a world that acquires nothing at boot.
          libraryRequired: lib?.required ?? 0,
          libraryBound: (lib?.resident ?? 0) + (lib?.broken ?? 0),
          // A CONDITIONAL on the live count, never a constant — see `BAR_MIN_STEPS`.
          determinate: determinate(r.required.length),
          opened: gateOpened,
          /*
           * L4 · IS THE PLAYER BEING HELD BEHIND THE PLATE RIGHT NOW?
           *
           * Read LIVE off the surface, and it answers the reachability question this whole stage
           * is judged by without a single pixel: `screen === true` for every sample taken while
           * `phase !== 'playing'`, and false once the level is playable. It goes false at the
           * REPARENT, not at the end of the fade — after that the element is decoration over a
           * world that is already ticking, and calling that a loading screen would make the field
           * mean "an element exists" instead of "the player is waiting".
           */
          screen: screen?.visible() ?? false,
          firstFrameAtMs,
          playableAtMs: load.enteredAt('playing'),
        };
      },
      ...(openEditorForHook ? { openEditor: openEditorForHook } : {}),
    });
  }

  return {
    unmount() {
      // The residency deadline outlives the loop otherwise: a 20 s timer firing into a torn-down
      // mount would open a gate on a world that no longer exists (and keep the closure alive).
      if (residencyTimer !== null) clearTimeout(residencyTimer);
      if (bootTimer !== null) clearTimeout(bootTimer);
      // The plate outlives the renderer otherwise: an unmount during a load would leave an opaque
      // full-viewport element on the one UI root, holding the keyboard for a game that is gone.
      screen?.dispose();
      perfBar?.dispose();
      // P1: abort every fetch in flight, dispose every source, forget every handle. The scene's
      // own dispose has already released the character's handles; this is the backstop for
      // anything a caller forgot and for the fetches that have not landed.
      // C: every retained placement handle and every live summon, before the service tears the
      // sources down under them.
      libraryEntities?.disposeAll();
      assets.dispose();
      window.removeEventListener('keydown', onPerfBar);
      disposeDevHook?.();
      disposeGiBadge?.();
      disposeDevBackend?.();
      scene.renderer.setAnimationLoop(null);
      window.removeEventListener('keydown', onPresetSwap);
      window.removeEventListener('keydown', onTuning);
      window.removeEventListener('keydown', onF2);
      editorBadge?.dispose();
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
      detachKeys();
      detachMouse();
      axonSpike?.dispose();
      exploration?.dispose();
      menu?.dispose();
      editor?.dispose();
      host.dispose();
      sfx.dispose();
      pane?.dispose();
      scene.dispose();
    },
  };
}
